import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Pagination,
  Stack,
  Chip,
  IconButton,
  Alert,
  Tooltip,
  Grid,
  LinearProgress,
  Divider,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  LocalShipping as LocalShippingIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
  SortByAlpha as SortIcon,
  Email as EmailIcon,
  ViewList as ViewListIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ShippingPreparationModal from '../components/ShippingPreparationModal';
import ProductImageGallery from '../components/ProductImageGallery';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import _ from 'lodash';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ExcelLikeField from '../components/ExcelLikeField';
import { INTL_REGION_LIST, isIntlOrder, intlCountryOf, intlRegionOf, isSfIntlOrder } from '../config/intlRegions';
import { io, Socket } from 'socket.io-client';

// Helper: get variant identifier for a storefront order item (matches DB composite key)
const getItemVariant = (item: { variant_title?: string | null; option1?: string | null; option2?: string | null }) => {
  if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
  const combined = [item.option1, item.option2].filter(Boolean).join(' / ');
  return combined || 'Default';
};

// Hover-zoom image: shows enlarged product image centered on screen when hovered (same as Shopify page)
const HoverZoomImage: React.FC<{ src: string; alt: string; width?: number; height?: number; borderColor?: string }> = ({
  src, alt, width = 60, height = 60, borderColor = '#e0e0e0'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);

  const renderZoomedImage = () => {
    if (!isHovered) return null;
    return (
      <>
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.4)', zIndex: 99998, pointerEvents: 'none', transition: 'opacity 0.3s ease-in-out' }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', width: '0px', height: '0px', zIndex: 99999, pointerEvents: 'none' }}>
          <img src={src} alt={alt} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '600px', height: '600px', objectFit: 'contain', backgroundColor: 'white', padding: '16px',
            borderRadius: '12px', boxShadow: '0 24px 96px rgba(0,0,0,0.7)', border: `4px solid ${borderColor}`,
            maxWidth: '90vw', maxHeight: '90vh', display: 'block' }} />
        </div>
      </>
    );
  };

  return (
    <>
      <img ref={imageRef} src={src} alt={alt}
        style={{ width: `${width}px`, height: `${height}px`, objectFit: 'contain', cursor: 'pointer', transition: 'opacity 0.2s ease' }}
        onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
        onClick={() => window.open(src, '_blank')} loading="lazy" />
      {typeof document !== 'undefined' && document.body &&
        require('react-dom').createPortal(renderZoomedImage(), document.body)}
    </>
  );
};

// ---- Interfaces ----

interface OrderItem {
  id: number;
  item_id: number;
  variant_id: string | null;
  title: string;
  price: number;
  discount_price: number;
  quantity: number;
  sku: string;
  item_type: string;
  variant_title: string | null;
  option1: string | null;
  option2: string | null;
  image_url: string | null;
  all_images?: string[] | string | null;
}

interface StorefrontOrder {
  id: number;
  order_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string | null;
  total_amount: number;
  status: string;
  delivery_method: string;
  payment_method: string;
  store_id: string | null;
  store_name: string | null;
  store_address: string | null;
  shipping_address: string | null;
  shipping_country: string | null;  // 'TW' (default) | 'HK' | 'MO' (intl regions — see config/intlRegions.ts)
  shipping_fee: number | null;
  // SF Express International (HK Phase 3 Apr 2026)
  sf_waybill_no: string | null;
  sf_label_url: string | null;
  sf_invoice_url: string | null;
  sf_customer_order_no: string | null;
  sf_cancelled_at: string | null;
  sf_created_at: string | null;
  sf_last_error: string | null;
  logistics_sub_type: string | null;
  order_source: string;
  created_at: string;
  updated_at: string;
  items: string | any[] | null; // Original cart items JSON with prices
  order_items: OrderItem[];
  // Logistics (from ecpay_logistics_orders join)
  tracking_number: string | null;
  cvs_payment_no: string | null;
  cvs_validation_no: string | null;
  elo_logistics_sub_type: string | null;
  ecpay_logistics_id: string | null;
  logistics_status: string | null;
  // Payment (from ecpay_payments join)
  payment_status: string | null;
  payment_trade_no: string | null;
  payment_merchant_trade_no: string | null;
  // Promo
  discount_amount: number | string | null;
  promo_id: number | null;
}

interface TrackingData {
  orderId: string;
  productName: string;
  variantTitle: string;
  quantity: number;
  received: boolean;
  outOfStock: boolean;
  missingCount: number;
  note: string;
  tag: string;
  referenceImage: string;
  receivedBy?: string;
  receivedAt?: string;
}

// ---- Styles (copied from ShopifyOrdersPage S object) ----

const S = {
  page: {
    width: '100%',
    minHeight: '100vh',
    bgcolor: '#ffffff',
    fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
    pb: 4,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2.5,
    px: { xs: 2, md: 3 },
    pt: { xs: 2, md: 2.5 },
    pb: 2,
    bgcolor: '#ffffff',
    borderBottom: '1px solid #e8e5e1',
    boxShadow: '0 1px 0 rgba(0,0,0,0.03)',
    borderRadius: { xs: 0, md: '0 0 16px 16px' },
    flexWrap: 'wrap' as const,
    gap: 1.5,
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: { xs: '1.35rem', md: '1.6rem' },
    color: '#1a1a1a',
    letterSpacing: '-0.03em',
    lineHeight: 1.2,
    fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
  },
  headerBtn: {
    borderRadius: '10px',
    borderColor: '#e8e5e1',
    color: '#5c5854',
    fontWeight: 600,
    textTransform: 'none' as const,
    fontSize: '0.85rem',
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: '#d1cdc8', bgcolor: 'rgba(0,0,0,0.02)' },
  },
  filterPanel: {
    p: { xs: 2, md: 2.5 },
    mx: { xs: 2, md: 3 },
    mb: 2,
    border: '1px solid #e8e5e1',
    borderRadius: '14px',
    bgcolor: '#fafaf8',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#ffffff',
      fontSize: '1.05rem',
      transition: 'none',
      fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
      '& fieldset': { borderColor: '#e8e5e1' },
      '&:hover fieldset': { borderColor: '#d1cdc8' },
      '&.Mui-focused fieldset': { borderColor: '#8c8580', borderWidth: '1.5px' },
    },
  },
  filterSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: '#e8e5e1' },
      '&:hover fieldset': { borderColor: '#d1cdc8' },
      '&.Mui-focused fieldset': { borderColor: '#8c8580' },
    },
  },
  orderCard: (status: 'ready' | 'partial' | 'pending' | 'shipped') => ({
    borderRadius: '16px',
    border: '1px solid #e8e5e1',
    bgcolor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 'fit-content',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    borderLeft: status === 'ready' ? '4px solid #22c55e'
      : status === 'partial' ? '4px solid #f59e0b'
      : status === 'shipped' ? '4px solid #3b82f6'
      : '4px solid transparent',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
      transform: 'translateY(-1px)',
    },
    '@media (max-width: 600px)': { minHeight: 'auto' },
  }),
  statusChip: {
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 26,
    borderRadius: '8px',
    letterSpacing: '0.02em',
  },
  tableHeader: {
    bgcolor: '#fafaf8',
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: '#8c8580',
      borderBottom: '1px solid #e8e5e1',
      py: 1.5,
      px: 2,
      fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
    },
  },
  tableRow: (received: boolean) => ({
    bgcolor: received ? '#f0fdf4' : 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      bgcolor: received ? '#dcfce7' : '#fafaf8',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #f3f1ee',
      py: 2,
      px: 2,
      fontSize: '0.9rem',
    },
  }),
  mono: {
    fontFamily: '"DM Mono", "SF Mono", monospace',
  },
  tabContainer: {
    bgcolor: '#f0ede9',
    p: '3px',
    borderRadius: '12px',
    display: 'inline-flex',
    gap: '3px',
  },
  tabButton: (active: boolean) => ({
    borderRadius: '10px',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
    bgcolor: active ? '#ffffff' : 'transparent',
    color: active ? '#1a1a1a' : '#8c8580',
    px: { xs: 2, md: 2.5 },
    py: 0.75,
    fontWeight: active ? 700 : 500,
    textTransform: 'none' as const,
    minWidth: 72,
    fontSize: '0.875rem',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      bgcolor: active ? '#ffffff' : 'rgba(0,0,0,0.04)',
      color: active ? '#1a1a1a' : '#5c5854',
    },
  }),
};

// ---- Status labels ----
const STATUS_LABELS: Record<string, string> = {
  unshipped: '未出貨',
  PENDING: '待處理',
  PENDING_PAYMENT: '待付款',
  WAITING_PAYMENT: '待付款',
  PAID: '已付款',
  ORDERED: '已下單韓國',
  SHIPPED: '已出貨',
  DELIVERED: '已送達',
  CANCELLED: '已取消',
};

const STATUS_OPTIONS = ['unshipped', 'PENDING', 'PENDING_PAYMENT', 'PAID', 'ORDERED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

function getCardStatus(allReceived: boolean, receivedCount: number, totalItems: number, orderStatus: string): 'ready' | 'partial' | 'pending' | 'shipped' {
  if (orderStatus === 'SHIPPED' || orderStatus === 'DELIVERED') return 'shipped';
  if (allReceived && totalItems > 0) return 'ready';
  if (receivedCount > 0) return 'partial';
  return 'pending';
}

// ---- Component ----

const StorefrontOrdersPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const socketRef = useRef<Socket | null>(null);

  // Data
  const [orders, setOrders] = useState<StorefrontOrder[]>([]);
  const [tracking, setTracking] = useState<Map<string, TrackingData>>(new Map());
  const [productMetadata, setProductMetadata] = useState<{ [key: string]: { tag: string; reference_image?: string } }>({});
  const [loading, setLoading] = useState(false);

  // UI state — filters (same as Shopify)
  const [statusFilter, setStatusFilter] = useState<string>('unshipped');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<string>('all');
  const [colorFilter, setColorFilter] = useState<string>('All');
  const [sizeFilter, setSizeFilter] = useState<string>('All');
  const [tagFilter, setTagFilter] = useState<string>('All');
  const [sortOrder, setSortOrder] = useState<string>('oldest');
  const [receivedFilter, setReceivedFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all'); // 'all' | 'TW' | intl code ('HK' | 'MO' …) — see config/intlRegions.ts
  const [searchInput, setSearchInput] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  // Per-order gallery toggle
  const [galleryOrders, setGalleryOrders] = useState<Set<string>>(new Set());
  // Shipping dialog
  const [shippingDialogData, setShippingDialogData] = useState<any>(null);

  // Expand/collapse per order (same as Shopify — controls item-level filtering visibility)
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // 叫貨 mode state
  const [viewMode, setViewMode] = useState<'orders' | 'products'>('orders');
  const [allOrdersForGrouping, setAllOrdersForGrouping] = useState<StorefrontOrder[]>([]);
  const [loadingAllOrders, setLoadingAllOrders] = useState(false);
  const [expandedGroupOrderIds, setExpandedGroupOrderIds] = useState<Set<string>>(new Set());
  const [linkBatchIndex, setLinkBatchIndex] = useState(0);

  // Auto-compute last ordering cutoff: Wed 20:00 or Sun 20:00 (whichever is most recent)
  const getLastOrderingCutoff = useCallback(() => {
    const now = moment();
    const day = now.day(); // 0=Sun, 3=Wed
    const hour = now.hour();
    // Find the most recent Wed 20:00 and Sun 20:00, pick the later one
    const lastWed = moment().day(day >= 3 || (day === 3 && hour >= 20) ? 3 : 3 - 7).hour(20).minute(0).second(0);
    if (lastWed.isAfter(now)) lastWed.subtract(7, 'days');
    const lastSun = moment().day(day >= 0 ? 0 : 0 - 7).hour(20).minute(0).second(0);
    if (lastSun.isAfter(now)) lastSun.subtract(7, 'days');
    return lastWed.isAfter(lastSun) ? lastWed : lastSun;
  }, []);
  const [groupingAfterDate, setGroupingAfterDate] = useState(() => getLastOrderingCutoff().format('YYYY-MM-DDTHH:mm'));

  const decodeHtmlEntities = useCallback((input: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = input;
    return textArea.value;
  }, []);

  // Status menu
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const [statusMenuOrderId, setStatusMenuOrderId] = useState<string>('');

  // 補寄 (Re-ship) state
  const [reshipDialogOpen, setReshipDialogOpen] = useState(false);
  const [reshipOrder, setReshipOrder] = useState<StorefrontOrder | null>(null);
  const [reshipPaymentType, setReshipPaymentType] = useState<'Y' | 'N'>('N'); // Y=貨到付款, N=取貨不用付款
  const [reshipCodAmount, setReshipCodAmount] = useState<string>(''); // 貨到付款金額

  // Refund (退刷) state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundOrderId, setRefundOrderId] = useState('');
  const [refundTradeNo, setRefundTradeNo] = useState('');
  const [refundMaxAmount, setRefundMaxAmount] = useState(0);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundFullAmount, setRefundFullAmount] = useState(true);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [refundPaymentMethod, setRefundPaymentMethod] = useState<string>('credit_card');

  // Tracking key helper
  const trackingKey = (orderId: string, productName: string, variantTitle: string) =>
    `${orderId}__${productName}__${variantTitle || 'Default'}`;

  // Compute order tracking stats
  const getOrderTrackingStats = useCallback((order: StorefrontOrder) => {
    let receivedCount = 0;
    let totalItems = 0;
    let hasAnyTrackingData = false;
    if (order.order_items) {
      for (const item of order.order_items) {
        totalItems += item.quantity;
        const key = trackingKey(order.order_id, item.title, getItemVariant(item));
        const t = tracking.get(key);
        if (t) hasAnyTrackingData = true;
        if (t?.received) receivedCount += item.quantity;
      }
    }
    const allReceived = totalItems > 0 && receivedCount === totalItems;
    return { receivedCount, totalItems, allReceived, hasAnyTrackingData };
  }, [tracking]);

  // Debounced search (same as Shopify page)
  const debouncedSearch = useCallback(
    _.debounce((searchTerm: string) => {
      setSearchFilter(searchTerm);
      setIsSearchPending(false);
    }, 500),
    []
  );

  // Product key: SKU if available, otherwise productName|variant
  const getProductKey = useCallback((sku: string, productName: string, itemSpec: string) => {
    if (sku && sku.trim()) return sku.trim();
    return `${productName}|${itemSpec || 'default'}`.toLowerCase().trim();
  }, []);

  // Fetch product metadata (tag/reference_image keyed by product_key)
  const fetchProductMetadata = useCallback(async () => {
    try {
      const res = await api.get('/api/products/metadata');
      const dict: { [key: string]: { tag: string; reference_image?: string } } = {};
      for (const item of (res.data || [])) {
        if (item.product_key) {
          dict[item.product_key] = { tag: item.tag || '', reference_image: item.reference_image };
        }
      }
      setProductMetadata(dict);
    } catch (err: any) {
      console.error('Failed to fetch product metadata:', err.message);
    }
  }, []);

  // ---- Data fetching ----
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all orders at once (like Shopify page) — filtering is client-side
      const params: Record<string, string> = { limit: '3000' };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/api/storefront/orders', { params });
      setOrders(res.data.orders || []);
      setTotalOrders(res.data.total || 0);
    } catch (err: any) {
      enqueueSnackbar(`Failed to fetch orders: ${err.message}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, enqueueSnackbar]);

  const fetchTracking = useCallback(async () => {
    try {
      const res = await api.get('/api/storefront/orders-tracking');
      const map = new Map<string, TrackingData>();
      for (const t of res.data) {
        map.set(trackingKey(t.orderId, t.productName, t.variantTitle), t);
      }
      setTracking(map);
    } catch (err: any) {
      console.error('Failed to fetch tracking:', err.message);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { fetchTracking(); }, [fetchTracking]);
  useEffect(() => { fetchProductMetadata(); }, [fetchProductMetadata]);

  // Reset to page 1 when any filter changes
  useEffect(() => {
    setPage(1);
  }, [searchFilter, fulfillmentFilter, colorFilter, sizeFilter, tagFilter, receivedFilter, countryFilter, sortOrder]);

  // Cleanup debounced search on unmount
  useEffect(() => {
    return () => { debouncedSearch.cancel(); };
  }, [debouncedSearch]);

  // ---- Socket.io ----
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const socket = io(apiUrl, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => {
      socket.emit('join-shop', 'storefront');
    });
    socket.on('tracking-updated', (data: any) => {
      if (data.socketId === socket.id) return;
      setTracking(prev => {
        const next = new Map(prev);
        const key = trackingKey(data.orderId, data.productName, data.itemSpec || 'Default');
        const existing = next.get(key);
        next.set(key, {
          orderId: data.orderId, productName: data.productName,
          variantTitle: data.itemSpec || 'Default', quantity: existing?.quantity || 1,
          received: data.changes.received, outOfStock: data.changes.outOfStock || false,
          missingCount: data.changes.missingCount,
          note: data.changes.note, tag: data.changes.tag,
          referenceImage: data.changes.referenceImage || '',
          receivedBy: data.changes.receivedBy, receivedAt: data.changes.receivedAt,
        });
        return next;
      });
    });
    socket.on('order-status-updated', (data: any) => {
      setOrders(prev => prev.map(o => o.order_id === data.orderId ? {
        ...o,
        status: data.status,
        ...(data.tracking_number !== undefined && { tracking_number: data.tracking_number }),
        ...(data.cvs_payment_no !== undefined && { cvs_payment_no: data.cvs_payment_no }),
        ...(data.cvs_validation_no !== undefined && { cvs_validation_no: data.cvs_validation_no }),
        ...(data.ecpay_logistics_id !== undefined && { ecpay_logistics_id: data.ecpay_logistics_id }),
        ...(data.elo_logistics_sub_type !== undefined && { elo_logistics_sub_type: data.elo_logistics_sub_type }),
      } : o));
    });
    socketRef.current = socket;
    return () => { socket.disconnect(); };
  }, []);

  // ---- Handlers ----
  const handleTrackingUpdate = async (orderId: string, productName: string, variantTitle: string, field: string, value: any) => {
    const key = trackingKey(orderId, productName, variantTitle);
    const existing = tracking.get(key) || {
      orderId, productName, variantTitle, quantity: 1,
      received: false, outOfStock: false, missingCount: 0, note: '', tag: '', referenceImage: '',
    };
    const updated = { ...existing, [field]: value };
    setTracking(prev => { const next = new Map(prev); next.set(key, updated); return next; });
    try {
      await api.post('/api/storefront/orders-tracking', {
        orderId, productName, variantTitle: variantTitle || 'Default',
        received: updated.received, outOfStock: updated.outOfStock,
        missingCount: updated.missingCount,
        note: updated.note, tag: updated.tag, referenceImage: updated.referenceImage,
        userId: currentUser?.id, userName: currentUser?.name,
        socketId: socketRef.current?.id,
      });
    } catch (err: any) {
      enqueueSnackbar(`Failed to save tracking: ${err.message}`, { variant: 'error' });
      setTracking(prev => { const next = new Map(prev); next.set(key, existing); return next; });
    }
  };

  // Tag update — saves to product_metadata table (same as Shopify)
  const handleTagUpdate = useCallback(async (item: OrderItem, value: string) => {
    const productKey = getProductKey(item.sku || '', item.title, item.variant_title || '');

    // Optimistic update
    setProductMetadata(prev => ({
      ...prev,
      [productKey]: { ...prev[productKey], tag: value },
    }));

    try {
      await api.post('/api/products/metadata', { productKey, tag: value });
    } catch (err: any) {
      enqueueSnackbar('Failed to update tag', { variant: 'error' });
    }
  }, [getProductKey, enqueueSnackbar]);

  // Handle variant/size updates (e.g. change L to XL)
  const handleVariantUpdate = useCallback(async (orderId: string, item: OrderItem, newVariant: string) => {
    const trimmed = newVariant.trim();
    if (!trimmed) return;
    const currentVariant = item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : [item.option1, item.option2].filter(Boolean).join(' / ') || '-';
    if (trimmed === currentVariant) return;

    // Optimistic update
    setOrders(prev => prev.map(o => {
      if (o.order_id !== orderId) return o;
      return {
        ...o,
        order_items: o.order_items?.map(it => it.id === item.id ? { ...it, variant_title: trimmed } : it),
      };
    }));
    try {
      await api.patch(`/api/storefront/order-items/${item.id}/variant`, { variant_title: trimmed });
      enqueueSnackbar(`規格已更新: ${trimmed}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`更新規格失敗: ${err.message}`, { variant: 'error' });
      // Revert
      setOrders(prev => prev.map(o => {
        if (o.order_id !== orderId) return o;
        return {
          ...o,
          order_items: o.order_items?.map(it => it.id === item.id ? { ...it, variant_title: item.variant_title } : it),
        };
      }));
    }
  }, [enqueueSnackbar]);

  const handleSkuUpdate = useCallback(async (orderId: string, item: OrderItem, newSku: string) => {
    const trimmed = newSku.trim();
    // Optimistic update
    setOrders(prev => prev.map(o => {
      if (o.order_id !== orderId) return o;
      return {
        ...o,
        order_items: o.order_items?.map(it => it.id === item.id ? { ...it, sku: trimmed } : it),
      };
    }));
    try {
      await api.patch(`/api/storefront/order-items/${item.id}/sku`, { sku: trimmed });
      enqueueSnackbar(`品號已更新: ${trimmed || '(空)'}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`更新品號失敗: ${err.message}`, { variant: 'error' });
      // Revert on failure
      setOrders(prev => prev.map(o => {
        if (o.order_id !== orderId) return o;
        return {
          ...o,
          order_items: o.order_items?.map(it => it.id === item.id ? { ...it, sku: item.sku } : it),
        };
      }));
    }
  }, [enqueueSnackbar]);

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      await api.patch(`/api/storefront/orders/${orderId}/status`, { status: newStatus });
      setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, status: newStatus } : o));
      enqueueSnackbar(`${orderId} → ${STATUS_LABELS[newStatus] || newStatus}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`Failed to update status: ${err.message}`, { variant: 'error' });
    }
    setStatusMenuAnchor(null);
    setStatusMenuOrderId('');
  };

  // 退刷 (Refund) handlers
  const openRefundDialog = (orderId: string, tradeNo: string, maxAmount: number, paymentMethod?: string) => {
    setRefundOrderId(orderId);
    setRefundTradeNo(tradeNo);
    setRefundMaxAmount(maxAmount);
    setRefundAmount(String(maxAmount));
    setRefundFullAmount(true);
    setRefundPaymentMethod(paymentMethod || 'credit_card');
    setRefundDialogOpen(true);
  };

  const handleRefund = async () => {
    const amount = parseInt(refundAmount, 10);
    if (!amount || amount <= 0) {
      enqueueSnackbar('請輸入有效的退刷金額', { variant: 'warning' });
      return;
    }
    if (amount > refundMaxAmount) {
      enqueueSnackbar(`退刷金額不得超過訂單金額 $${refundMaxAmount}`, { variant: 'warning' });
      return;
    }

    setProcessingRefund(true);
    try {
      // Route to correct refund endpoint based on payment method
      const refundUrl = refundPaymentMethod === 'line_pay'
        ? `/api/linepay/refund/${refundOrderId}`
        : `/api/ecpg/payment/refund/${refundOrderId}`;
      const response = await api.post(refundUrl, {
        refundAmount: amount
      });

      if (response.data.success) {
        const isFullRefund = amount >= refundMaxAmount;
        enqueueSnackbar(`退刷成功 - 金額: $${amount}${isFullRefund ? '（全額）' : '（部分）'}`, { variant: 'success' });
        setOrders(prev => prev.map(o =>
          o.order_id === refundOrderId
            ? { ...o, payment_status: 'REFUNDED', ...(isFullRefund ? { status: 'CANCELLED' } : {}) }
            : o
        ));
        setRefundDialogOpen(false);
      } else {
        enqueueSnackbar(`退刷失敗: ${response.data.rtnMsg || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.message || '未知錯誤';
      enqueueSnackbar(`退刷失敗: ${errMsg}`, { variant: 'error' });
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, orderId: string, productName: string, variantTitle: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      handleTrackingUpdate(orderId, productName, variantTitle, 'referenceImage', base64);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveReferenceImage = (orderId: string, productName: string, variantTitle: string) => {
    handleTrackingUpdate(orderId, productName, variantTitle, 'referenceImage', '');
  };

  // Search handlers (same pattern as Shopify page)
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (value !== searchFilter) setIsSearchPending(true);
    debouncedSearch(value);
  }, [debouncedSearch, searchFilter]);

  const triggerSearch = useCallback(() => {
    debouncedSearch.cancel();
    setSearchFilter(searchInput);
    setIsSearchPending(false);
  }, [searchInput, debouncedSearch]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchFilter('');
    setIsSearchPending(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Parse original cart items JSON for price lookup (order_items may have 0 prices)
  const getCartItemPrice = (order: StorefrontOrder, itemId: number, variantId: string | null): { price: number; discountPrice: number } => {
    try {
      const cartItems: any[] = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
      const match = cartItems.find((ci: any) =>
        (ci.itemId === itemId || ci.id === itemId) &&
        (!variantId || ci.variantId === variantId || ci.variant_id === variantId)
      ) || cartItems.find((ci: any) => ci.itemId === itemId || ci.id === itemId);
      if (match) {
        return {
          price: parseFloat(match.price || match.compare_at_price || 0),
          discountPrice: parseFloat(match.discountPrice || match.discount_price || match.price || 0),
        };
      }
    } catch {}
    return { price: 0, discountPrice: 0 };
  };

  // Build order object compatible with ShippingPreparationModal / HomeDeliveryModal
  const buildShippingOrder = (order: StorefrontOrder) => ({
    orderId: order.order_id,
    orderNumber: order.order_id,
    customer: order.customer_name,
    store_id: order.store_id,
    store_name: order.store_name,
    store_address: order.store_address,
    logistics_sub_type: order.logistics_sub_type || order.elo_logistics_sub_type,
    deliveryMethod: order.delivery_method === 'pickup' ? 'pickup' : 'home_delivery',
    shippingAddress: order.delivery_method === 'shipping' ? (order.shipping_address || '') : (order.store_address || ''),
    status: !(order.payment_method || '').toLowerCase().includes('cod') ? 'paid' : 'pending',
    tracking_number: order.tracking_number,
    payment_no: order.cvs_payment_no,
    cvs_validation_no: order.cvs_validation_no,
    ecpay_logistics_id: order.ecpay_logistics_id,
    hasTrackingInfo: !!(order.tracking_number || order.cvs_payment_no),
    items: order.order_items?.map(item => {
      const cartPrice = getCartItemPrice(order, item.item_id, item.variant_id);
      // Cart has the actual transaction prices; DB prices may be catalog prices
      const origPrice = cartPrice.price || item.price || 0;
      const sellingPrice = cartPrice.discountPrice || cartPrice.price || item.discount_price || item.price || 0;
      const discount = origPrice > sellingPrice ? (origPrice - sellingPrice) * item.quantity : 0;
      return {
        id: item.id,
        productName: item.title,
        title: item.title,
        itemSpec: item.variant_title || '-',
        quantity: item.quantity,
        price: String(sellingPrice),
        total_discount: String(discount),
      };
    }) || [],
    discountAmount: parseFloat(String(order.discount_amount || 0)),
    original: {
      current_total_price: String(order.total_amount || 0),
      financial_status: !(order.payment_method || '').toLowerCase().includes('cod') ? 'paid' : 'pending',
      line_items: order.order_items?.map(item => {
        const cp = getCartItemPrice(order, item.item_id, item.variant_id);
        const selling = cp.discountPrice || cp.price || item.discount_price || item.price || 0;
        const orig = cp.price || item.price || selling;
        return {
          title: item.title,
          quantity: item.quantity,
          price: String(selling),
          total_discount: String(orig > selling ? (orig - selling) * item.quantity : 0),
        };
      }) || [],
      shipping_address: {
        first_name: order.customer_name,
        last_name: '',
        phone: order.customer_phone,
        address1: order.delivery_method === 'shipping' ? (order.shipping_address || '') : (order.store_address || ''),
      },
      customer: {
        email: order.customer_email || '',
        phone: order.customer_phone,
      },
    },
    allItemsReceived: (() => {
      const { allReceived } = getOrderTrackingStats(order);
      return allReceived;
    })(),
    totalItemsInOrder: order.order_items?.reduce((sum, i) => sum + i.quantity, 0) || 0,
  });

  // Quick ship for convenience store (same as Shopify handleQuickShip)
  const handleQuickShip = useCallback(async (order: StorefrontOrder) => {
    try {
      enqueueSnackbar('正在快速建立物流單...', { variant: 'info' });

      if (!order.store_id) {
        enqueueSnackbar('訂單缺少超商門市資訊', { variant: 'error' });
        return;
      }

      const logisticsType = order.logistics_sub_type || order.elo_logistics_sub_type ||
        (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id) ? 'FAMIC2C' : 'UNIMARTC2C');

      const totalAmount = Math.floor(order.total_amount) || 100;

      const formatPhone = (phone: string) => {
        let clean = phone.replace(/\D/g, '');
        if (clean.startsWith('886')) clean = '0' + clean.substring(3);
        if (!clean.startsWith('0') && clean.length === 9) clean = '0' + clean;
        return clean;
      };

      const customerName = (order.customer_name || '顧客').substring(0, 10);
      const customerPhone = formatPhone(order.customer_phone || '');

      if (!customerPhone || customerPhone.length !== 10) {
        enqueueSnackbar('客戶電話號碼無效（需要10位數字）', { variant: 'error' });
        return;
      }

      const itemsDesc = order.order_items?.slice(0, 3).map(i => i.title.split(' ')[0]).join(', ') || 'Goods';
      const goodsName = itemsDesc.length > 25 ? itemsDesc.substring(0, 22) + '...' : itemsDesc;

      const response = await api.post('/api/ecpay/logistics/create-for-shopify', {
        orderId: order.order_id,
        storeId: order.store_id,
        customerName,
        customerPhone,
        customerEmail: order.customer_email || '',
        logisticsSubType: logisticsType,
        goodsAmount: totalAmount,
        storeName: order.store_name || '',
        isCollection: !(order.payment_method || '').toLowerCase().includes('cod') ? 'N' : 'Y',
        goodsName,
        source: 'storefront_quick',
      });

      if (response.data?.success) {
        let displayTN = '';
        if (response.data.trackingNumber) displayTN = response.data.trackingNumber;
        else if (response.data.paymentNo) {
          displayTN = logisticsType === 'FAMIC2C' ? response.data.paymentNo
            : response.data.validationNo ? `${response.data.paymentNo}${response.data.validationNo}` : response.data.paymentNo;
        }

        // Update order status to SHIPPED (include tracking number for email)
        const displayTN2 = response.data.trackingNumber
          || (response.data.validationNo ? `${response.data.paymentNo}${response.data.validationNo}` : response.data.paymentNo);
        try {
          await api.patch(`/api/storefront/orders/${order.order_id}/status`, { status: 'SHIPPED', trackingNumber: displayTN2 });
        } catch (statusErr) {
          console.warn('Failed to update order status to SHIPPED:', statusErr);
        }

        setOrders(prev => prev.map(o => o.order_id === order.order_id ? {
          ...o,
          status: 'SHIPPED',
          tracking_number: response.data.trackingNumber || null,
          cvs_payment_no: response.data.paymentNo || null,
          cvs_validation_no: response.data.validationNo || null,
          ecpay_logistics_id: response.data.logisticsId || null,
          elo_logistics_sub_type: logisticsType,
        } : o));

        enqueueSnackbar(`快速出貨成功！追蹤號碼: ${displayTN}`, { variant: 'success', autoHideDuration: 6000 });
      } else {
        enqueueSnackbar(`建立物流單失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      console.error('Quick ship error:', err);
      enqueueSnackbar(`快速出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, getOrderTrackingStats]);

  // 補寄 (Re-ship) — creates new ECPay logistics with suffixed orderId
  const handleReship = useCallback(async () => {
    const order = reshipOrder;
    if (!order) return;

    try {
      enqueueSnackbar('正在建立補寄物流單...', { variant: 'info' });

      if (!order.store_id) {
        enqueueSnackbar('訂單缺少超商門市資訊', { variant: 'error' });
        return;
      }

      const logisticsType = order.logistics_sub_type || order.elo_logistics_sub_type ||
        (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id) ? 'FAMIC2C' : 'UNIMARTC2C');

      const totalAmount = Math.floor(order.total_amount) || 100;

      const formatPhone = (phone: string) => {
        let clean = phone.replace(/\D/g, '');
        if (clean.startsWith('886')) clean = '0' + clean.substring(3);
        if (!clean.startsWith('0') && clean.length === 9) clean = '0' + clean;
        return clean;
      };

      const customerName = (order.customer_name || '顧客').substring(0, 10);
      const customerPhone = formatPhone(order.customer_phone || '');

      if (!customerPhone || customerPhone.length !== 10) {
        enqueueSnackbar('客戶電話號碼無效（需要10位數字）', { variant: 'error' });
        return;
      }

      const itemsDesc = order.order_items?.slice(0, 3).map(i => i.title.split(' ')[0]).join(', ') || 'Goods';
      const goodsName = itemsDesc.length > 25 ? itemsDesc.substring(0, 22) + '...' : itemsDesc;

      // Generate suffixed orderId to avoid ECPay duplicate error
      // Only strip reship suffix (1-2 digit tail) if order already has one (3+ segments)
      const parts = order.order_id.split('-');
      const baseId = parts.length >= 3 && /^\d{1,2}$/.test(parts[parts.length - 1])
        ? parts.slice(0, -1).join('-')
        : order.order_id;
      let suffix = 1;
      const existingIds = new Set(orders.map(o => o.order_id));
      while (existingIds.has(`${baseId}-${suffix}`)) suffix++;
      const reshipOrderId = `${baseId}-${suffix}`;

      const response = await api.post('/api/ecpay/logistics/create-for-shopify', {
        orderId: reshipOrderId,
        storeId: order.store_id,
        customerName,
        customerPhone,
        customerEmail: order.customer_email || '',
        logisticsSubType: logisticsType,
        goodsAmount: reshipPaymentType === 'Y' ? (parseInt(reshipCodAmount, 10) || totalAmount) : totalAmount,
        storeName: order.store_name || '',
        isCollection: reshipPaymentType,
        goodsName: `[補寄] ${goodsName}`,
        source: 'storefront_reship',
      });

      if (response.data?.success) {
        let displayTN = '';
        if (response.data.trackingNumber) displayTN = response.data.trackingNumber;
        else if (response.data.paymentNo) {
          displayTN = logisticsType === 'FAMIC2C' ? response.data.paymentNo
            : response.data.validationNo ? `${response.data.paymentNo}${response.data.validationNo}` : response.data.paymentNo;
        }

        enqueueSnackbar(
          `補寄成功！寄件編號: ${reshipOrderId}，追蹤號碼: ${displayTN}`,
          { variant: 'success', autoHideDuration: 8000 }
        );

        // Auto-print shipping label for the new reship order
        try {
          enqueueSnackbar('正在準備補寄出貨單...', { variant: 'info' });
          const printResponse = await api.post(`/api/ecpay/logistics/print/${reshipOrderId}`, {
            platform: 'storefront',
            logistics_sub_type: logisticsType,
          });
          if (printResponse?.data) {
            const blob = new Blob([printResponse.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 100);
            enqueueSnackbar('補寄出貨單已在新視窗中開啟', { variant: 'success' });
          }
        } catch (printErr: any) {
          console.warn('Auto-print reship label failed:', printErr);
          enqueueSnackbar('補寄成功但無法自動列印出貨單，請手動列印', { variant: 'warning' });
        }
      } else {
        enqueueSnackbar(`補寄失敗: ${response.data?.error?.message || response.data?.message || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      console.error('Reship error:', err);
      enqueueSnackbar(`補寄失敗: ${err.response?.data?.error?.message || err.response?.data?.message || err.message}`, { variant: 'error' });
    } finally {
      setReshipDialogOpen(false);
      setReshipOrder(null);
    }
  }, [reshipOrder, reshipPaymentType, reshipCodAmount, orders, enqueueSnackbar]);

  // Revert SHIPPED → PAID (退回未出貨)
  const handleRevertShipped = useCallback(async (order: StorefrontOrder) => {
    if (!window.confirm(`確定要將 ${order.order_id} 從「已出貨」退回「未出貨」？`)) return;
    try {
      const targetStatus = order.payment_status === 'SUCCESS' ? 'PAID' : 'PENDING';
      await api.patch(`/api/storefront/orders/${order.order_id}/status`, { status: targetStatus });
      setOrders(prev => prev.map(o => o.order_id === order.order_id ? { ...o, status: targetStatus } : o));
      enqueueSnackbar(`${order.order_id} 已退回「${STATUS_LABELS[targetStatus] || targetStatus}」`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`退回失敗: ${err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Print shipping label
  const handlePrintShippingDocument = useCallback(async (order: StorefrontOrder) => {
    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info' });
      const logisticsType = order.elo_logistics_sub_type || order.logistics_sub_type;
      const response = await api.post(`/api/ecpay/logistics/print/${order.order_id}`, {
        platform: 'storefront',
        logistics_sub_type: logisticsType,
      });
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 100);
        enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });
      }
    } catch (err: any) {
      enqueueSnackbar(`無法產生出貨單: ${err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // hex image → base64 (same as HomeDeliveryModal)
  const hexToBase64 = (hexString: string): string => {
    let binaryString = '';
    for (let i = 0; i < hexString.length; i += 2) {
      binaryString += String.fromCharCode(parseInt(hexString.substr(i, 2), 16));
    }
    return btoa(binaryString);
  };

  // Display shipping label in new window (copied from HomeDeliveryModal.displayShippingLabel)
  const displayShippingLabel = (hexImage: string, labelData: { trackingNumber?: string; recipientName?: string; recipientAddress?: string; weight?: number } = {}) => {
    if (!hexImage) return;
    const base64Image = hexToBase64(hexImage);
    const newWindow = window.open('', '_blank');
    if (!newWindow) { alert('Please allow popups to view the shipping label'); return; }

    newWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>宅配出貨單 - ${labelData.trackingNumber || '追蹤號碼'}</title>
      <style>
        body { margin:0; padding:20px; font-family:Arial,sans-serif; background-color:#f5f5f5; display:flex; flex-direction:column; align-items:center; }
        .header { text-align:center; margin-bottom:20px; background:white; padding:15px; border-radius:8px; box-shadow:0 2px 4px rgba(0,0,0,0.1); width:100%; max-width:800px; }
        .header h1 { color:#2c3e50; margin:0 0 10px 0; }
        .tracking-info { display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:10px; margin-top:15px; text-align:left; }
        .info-item { background:#f8f9fa; padding:8px 12px; border-radius:4px; border-left:3px solid #3498db; }
        .info-label { font-weight:bold; color:#2c3e50; font-size:0.9em; }
        .info-value { color:#34495e; margin-top:2px; }
        .label-container { background:white; padding:20px; border-radius:8px; box-shadow:0 2px 4px rgba(0,0,0,0.1); max-width:100%; overflow:auto; }
        .shipping-label { max-width:100%; height:auto; border:1px solid #ddd; border-radius:4px; }
        .actions { margin-top:20px; text-align:center; }
        .btn { background:#3498db; color:white; border:none; padding:10px 20px; border-radius:4px; cursor:pointer; font-size:14px; margin:0 5px; transition:background-color 0.3s; }
        .btn:hover { background:#2980b9; }
        .btn-print { background:#27ae60; } .btn-print:hover { background:#229954; }
        .btn-download { background:#e74c3c; } .btn-download:hover { background:#c0392b; }
        @media print { body{background:white;padding:0;} .header,.actions{display:none;} .label-container{box-shadow:none;padding:0;margin:0;} }
      </style></head><body>
      <div class="header">
        <h1>🚚 宅配出貨單</h1><p>新竹物流 Hsinchu Logistics</p>
        ${labelData.trackingNumber ? `<div class="tracking-info">
          <div class="info-item"><div class="info-label">追蹤號碼</div><div class="info-value">${labelData.trackingNumber}</div></div>
          ${labelData.recipientName ? `<div class="info-item"><div class="info-label">收件人</div><div class="info-value">${labelData.recipientName}</div></div>` : ''}
          ${labelData.recipientAddress ? `<div class="info-item"><div class="info-label">收件地址</div><div class="info-value">${labelData.recipientAddress}</div></div>` : ''}
          ${labelData.weight ? `<div class="info-item"><div class="info-label">重量</div><div class="info-value">${labelData.weight} kg</div></div>` : ''}
        </div>` : ''}
      </div>
      <div class="label-container"><img src="data:image/png;base64,${base64Image}" alt="Shipping Label" class="shipping-label"></div>
      <div class="actions">
        <button class="btn btn-print" onclick="window.print()">🖨️ 列印</button>
        <button class="btn btn-download" onclick="var a=document.createElement('a');a.href='data:image/png;base64,${base64Image}';a.download='宅配出貨單_${labelData.trackingNumber || Date.now()}.png';a.click();">💾 下載</button>
        <button class="btn" onclick="window.close()">❌ 關閉</button>
      </div></body></html>`);
    newWindow.document.close();
  };

  // Hsinchu home delivery — call API directly (same as Shopify handleHsinchuShipping)
  const handleHsinchuShipping = useCallback(async (order: StorefrontOrder) => {
    try {
      enqueueSnackbar('正在建立宅配出貨單...', { variant: 'info' });

      const formatPhone = (phone: string) => {
        let clean = phone.replace(/\D/g, '');
        if (clean.startsWith('886')) clean = '0' + clean.substring(3);
        if (!clean.startsWith('0') && clean.length === 9) clean = '0' + clean;
        return clean;
      };

      const address = order.shipping_address || order.store_address || '';
      if (!address) {
        enqueueSnackbar('缺少收件人地址', { variant: 'error' });
        return;
      }

      const cleanAddress = address.replace(/, Taiwan,?/gi, '').replace(/,\s*$/, '').trim();

      const response = await api.post('/api/hsinchu/create-order', {
        orderId: order.order_id,
        recipientName: order.customer_name || '顧客',
        recipientPhone: formatPhone(order.customer_phone || ''),
        recipientAddress: cleanAddress,
        weight: 1,
        itemCount: order.order_items?.length || 1,
        notes: '',
        declaredValue: 0,
        productType: '001',
      });

      if (response.data.success && response.data.data) {
        const result = response.data.data;

        // Update order status to SHIPPED (include tracking number for email)
        try {
          await api.patch(`/api/storefront/orders/${order.order_id}/status`, { status: 'SHIPPED', trackingNumber: result.trackingNumber });
        } catch (statusErr) {
          console.warn('Failed to update order status to SHIPPED:', statusErr);
        }

        setOrders(prev => prev.map(o =>
          o.order_id === order.order_id
            ? { ...o, status: 'SHIPPED', tracking_number: result.trackingNumber }
            : o
        ));

        enqueueSnackbar(`宅配出貨成功！追蹤號碼: ${result.trackingNumber}`, { variant: 'success', autoHideDuration: 6000 });

        // Display label using the same styled page as Shopify
        if (result.labelImage) {
          displayShippingLabel(result.labelImage, {
            trackingNumber: result.trackingNumber,
            recipientName: order.customer_name,
            recipientAddress: cleanAddress,
            weight: result.weight,
          });
        }
      } else {
        enqueueSnackbar(`建立宅配失敗: ${response.data?.error || '未知錯誤'}`, { variant: 'error' });
      }
    } catch (err: any) {
      console.error('Hsinchu shipping error:', err);
      enqueueSnackbar(`宅配出貨失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('已複製', { variant: 'success', autoHideDuration: 1500 });
  };

  // Get display tracking number for an order (same logic as Shopify page)
  const getDisplayTrackingNumber = (order: StorefrontOrder): string | null => {
    if (order.tracking_number) return order.tracking_number;
    if (!order.cvs_payment_no) return null;
    // FamilyMart uses just cvs_payment_no
    const subType = order.elo_logistics_sub_type || '';
    if (subType === 'FAMIC2C' ||
        (order.store_name && (order.store_name.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_name)))) {
      return order.cvs_payment_no;
    }
    // 7-11 uses cvs_payment_no + cvs_validation_no
    return order.cvs_validation_no
      ? `${order.cvs_payment_no}${order.cvs_validation_no}`
      : order.cvs_payment_no;
  };

  // ---- Summary stats ----
  const stats = useMemo(() => {
    let pending = 0, partial = 0, ready = 0, shipped = 0;
    for (const o of orders) {
      if (o.status === 'SHIPPED' || o.status === 'DELIVERED') { shipped++; continue; }
      const { allReceived, receivedCount } = getOrderTrackingStats(o);
      if (allReceived) ready++;
      else if (receivedCount > 0) partial++;
      else pending++;
    }
    return { total: orders.length, pending, partial, ready, shipped };
  }, [orders, getOrderTrackingStats]);

  // Extract unique colors and sizes from order items
  const getUniqueColorsAndSizes = useMemo(() => {
    const colors = new Set<string>();
    const sizes = new Set<string>();
    for (const o of orders) {
      for (const item of (o.order_items || [])) {
        if (item.option1) colors.add(item.option1);
        if (item.option2) sizes.add(item.option2);
      }
    }
    return { colors: Array.from(colors).sort(), sizes: Array.from(sizes).sort() };
  }, [orders]);

  const hasNumbersOnly = (s: string) => /^\d+$/.test(s.trim());

  const clearAllFilters = useCallback(() => {
    setStatusFilter('unshipped');
    setFulfillmentFilter('all');
    setColorFilter('All');
    setSizeFilter('All');
    setTagFilter('All');
    setSortOrder('newest');
    setReceivedFilter('all');
    setCountryFilter('all');
    setSearchInput('');
    setSearchFilter('');
    setIsSearchPending(false);
    debouncedSearch.cancel();
    setPage(1);
  }, [debouncedSearch]);

  const hasActiveFilters = statusFilter !== 'unshipped' || fulfillmentFilter !== 'all' ||
    colorFilter !== 'All' || sizeFilter !== 'All' || tagFilter !== 'All' ||
    receivedFilter !== 'all' || countryFilter !== 'all' || searchFilter;

  // ============================================================================
  // 叫貨 MODE — fetch all unshipped orders & group by product
  // ============================================================================

  const fetchAllOrdersForGrouping = useCallback(async () => {
    setLoadingAllOrders(true);
    try {
      const res = await api.get('/api/storefront/orders', { params: { status: 'unshipped', limit: '200' } });
      setAllOrdersForGrouping(res.data.orders || []);
    } catch (err: any) {
      enqueueSnackbar(`Failed to fetch orders for grouping: ${err.message}`, { variant: 'error' });
    } finally {
      setLoadingAllOrders(false);
    }
  }, [enqueueSnackbar]);

  // Fetch all orders when switching to 叫貨 mode
  useEffect(() => {
    if (viewMode === 'products') {
      fetchAllOrdersForGrouping();
    }
  }, [viewMode, fetchAllOrdersForGrouping]);

  // Group products for 叫貨 view
  const groupedProducts = useMemo(() => {
    if (viewMode !== 'products' || !allOrdersForGrouping.length) return [];

    const productMap = new Map<string, {
      sku: string;
      productName: string;
      imageUrl: string;
      totalQty: number;
      variants: { color: string; size: string; qty: number; orderNumber: string }[];
      orderNumbers: Set<string>;
    }>();

    const afterTimestamp = groupingAfterDate ? new Date(groupingAfterDate).getTime() : 0;

    allOrdersForGrouping.forEach(order => {
      // Skip orders before cutoff date
      if (afterTimestamp && new Date(order.created_at).getTime() < afterTimestamp) return;
      // Skip shipped/completed orders
      if (order.status === 'SHIPPED' || order.status === 'DELIVERED' || order.status === 'CANCELLED') return;

      (order.order_items || []).forEach((item: OrderItem) => {
        // Skip received items
        const tKey = trackingKey(order.order_id, item.title, getItemVariant(item));
        if (tracking.get(tKey)?.received) return;

        const key = item.sku || item.title;
        if (!key) return;

        const color = item.option1 || '預設';
        const size = item.option2 || '-';

        if (!productMap.has(key)) {
          productMap.set(key, {
            sku: item.sku || '',
            productName: item.title,
            imageUrl: item.image_url || '',
            totalQty: 0,
            variants: [],
            orderNumbers: new Set(),
          });
        }

        const group = productMap.get(key)!;
        group.totalQty += item.quantity;
        group.orderNumbers.add(order.order_id);
        group.variants.push({
          color,
          size,
          qty: item.quantity,
          orderNumber: order.order_id,
        });
      });
    });

    // Aggregate variants by color+size
    return Array.from(productMap.entries()).map(([key, group]) => {
      const specMap = new Map<string, { color: string; size: string; totalQty: number; orders: string[] }>();
      group.variants.forEach(v => {
        const specKey = `${v.color}|${v.size}`;
        if (!specMap.has(specKey)) {
          specMap.set(specKey, { color: v.color, size: v.size, totalQty: 0, orders: [] });
        }
        const s = specMap.get(specKey)!;
        s.totalQty += v.qty;
        if (!s.orders.includes(v.orderNumber)) s.orders.push(v.orderNumber);
      });

      const specs = Array.from(specMap.values());
      // Sort by color then size
      const sizeOrder: { [key: string]: number } = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6, '3XL': 7, '4XL': 8, '5XL': 9 };
      const getSizeRank = (size: string) => {
        const match = size.match(/^([\dX]*[SML]L?)/i);
        if (match && sizeOrder[match[1].toUpperCase()]) return sizeOrder[match[1].toUpperCase()];
        const num = parseInt(size);
        if (!isNaN(num)) return 100 + num;
        return 500;
      };
      specs.sort((a, b) => {
        const colorCmp = a.color.localeCompare(b.color, 'zh-TW');
        if (colorCmp !== 0) return colorCmp;
        return getSizeRank(a.size) - getSizeRank(b.size);
      });

      return {
        key,
        sku: group.sku,
        productName: group.productName,
        imageUrl: group.imageUrl,
        totalQty: group.totalQty,
        orderCount: group.orderNumbers.size,
        specs,
      };
    }).sort((a, b) => b.totalQty - a.totalQty);
  }, [viewMode, allOrdersForGrouping, tracking, groupingAfterDate]);

  // Resolve product links by SKU for order items view (same data source as Shopify page)
  const productLinksBySku = useMemo(() => {
    const linksMap = new Map<string, { link: string; allLinks?: string[] }>();
    try {
      const storedExcelData = localStorage.getItem('productLinksData');
      if (storedExcelData) {
        const productsList: any[] = JSON.parse(storedExcelData);
        productsList.forEach((p: any) => {
          if (p.id) {
            linksMap.set(p.id, {
              link: decodeHtmlEntities(p.link || (p.allLinks && p.allLinks[0]) || ''),
              allLinks: p.allLinks?.map((l: string) => decodeHtmlEntities(l)),
            });
          }
        });
      }
    } catch (e) { /* ignore */ }
    return linksMap;
  }, [decodeHtmlEntities]);

  // Resolve product links for grouped view
  const groupedProductLinks = useMemo(() => {
    if (viewMode !== 'products') return new Map<string, { link: string; allLinks?: string[] }>();
    const linksMap = new Map<string, { link: string; allLinks?: string[] }>();
    try {
      const storedExcelData = localStorage.getItem('productLinksData');
      if (storedExcelData) {
        const productsList: any[] = JSON.parse(storedExcelData);
        groupedProducts.forEach(group => {
          if (!group.sku) return;
          const match = productsList.find((p: any) => p.id === group.sku);
          if (match) {
            linksMap.set(group.key, {
              link: decodeHtmlEntities(match.link || (match.allLinks && match.allLinks[0]) || ''),
              allLinks: match.allLinks?.map((l: string) => decodeHtmlEntities(l)),
            });
          }
        });
      }
    } catch (e) { /* ignore */ }
    return linksMap;
  }, [viewMode, groupedProducts, decodeHtmlEntities]);

  // ============================================================================
  // 叫貨 PRODUCT GROUPING VIEW
  // ============================================================================

  const renderProductGroupingView = () => {
    if (loadingAllOrders) {
      return (
        <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>載入中...</Typography>
        </Box>
      );
    }

    if (groupedProducts.length === 0) {
      return (
        <Box sx={{ px: 3 }}>
          <Alert severity="info" sx={{ borderRadius: 3, border: '1px solid', borderColor: 'info.light' }}>
            <Typography variant="body2">
              目前沒有需要訂購的商品（所有商品都已收貨，或沒有未出貨訂單）
            </Typography>
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 3, py: 2, overflow: 'auto', height: '100%' }}>
        {/* Time Period Selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>篩選起始時間:</Typography>
          <TextField
            type="datetime-local"
            value={groupingAfterDate}
            onChange={(e) => setGroupingAfterDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
          />
          {groupingAfterDate && (
            <Button size="small" variant="text" onClick={() => setGroupingAfterDate('')} sx={{ textTransform: 'none', color: '#64748b', minWidth: 'auto' }}>
              清除
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {groupingAfterDate
              ? `顯示 ${moment(groupingAfterDate).format('YYYY/MM/DD HH:mm')} 之後的訂單`
              : '顯示全部訂單'}
          </Typography>
        </Box>

        {/* Summary + Batch Links — sticky bar */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', position: 'sticky', top: 0, zIndex: 10, bgcolor: 'white', py: 1, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="body1" color="text.secondary">
            共 <strong>{groupedProducts.length}</strong> 種商品需要訂購，
            合計 <strong>{groupedProducts.reduce((sum, g) => sum + g.totalQty, 0)}</strong> 件
          </Typography>
          {(() => {
            const allLinks = groupedProducts
              .map(g => groupedProductLinks.get(g.key))
              .map(info => info?.allLinks?.[0] || info?.link || '')
              .filter(Boolean);
            const remaining = allLinks.length - linkBatchIndex;
            const batchSize = Math.min(10, remaining);
            return allLinks.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={remaining <= 0}
                  onClick={async () => {
                    const batch = allLinks.slice(linkBatchIndex, linkBatchIndex + 10);
                    for (let i = 0; i < batch.length; i++) {
                      window.open(batch[i], '_blank', 'noopener,noreferrer');
                      if (i < batch.length - 1) await new Promise(r => setTimeout(r, 300));
                    }
                    setLinkBatchIndex(prev => prev + batch.length);
                    enqueueSnackbar(`開啟第 ${linkBatchIndex + 1}–${linkBatchIndex + batch.length} 個連結`, { variant: 'info' });
                  }}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  開啟下{batchSize}個連結 ({linkBatchIndex}/{allLinks.length})
                </Button>
                {linkBatchIndex > 0 && (
                  <Button
                    size="small"
                    onClick={() => setLinkBatchIndex(0)}
                    sx={{ textTransform: 'none', borderRadius: 2, color: '#64748b' }}
                  >
                    重置
                  </Button>
                )}
              </Box>
            ) : null;
          })()}
        </Box>

        {/* Product Cards */}
        {groupedProducts.map(group => {
          const linkInfo = groupedProductLinks.get(group.key);
          return (
            <React.Fragment key={group.key}>
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  border: '1px solid #e2e8f0',
                  bgcolor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    {/* Product Image */}
                    {group.imageUrl && (
                      <Box
                        component="img"
                        src={group.imageUrl}
                        alt={group.productName}
                        loading="lazy"
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: '1px solid #e2e8f0',
                          flexShrink: 0,
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(group.imageUrl, '_blank')}
                      />
                    )}

                    {/* Product Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }} noWrap>
                            {group.productName}
                          </Typography>
                          {group.sku && (
                            <Chip
                              label={group.sku}
                              size="small"
                              sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#f1f5f9', fontWeight: 600 }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          <Chip
                            icon={<ShoppingCartIcon sx={{ fontSize: 16 }} />}
                            label={`${group.totalQty} 件`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                          <Chip
                            label={`${group.orderCount} 筆訂單`}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />

                          {/* Factory Link Buttons */}
                          {linkInfo?.allLinks && linkInfo.allLinks.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {linkInfo.allLinks.map((link: string, idx: number) => (
                                <Tooltip key={idx} title={`開啟連結 ${idx + 1}`}>
                                  <IconButton
                                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                                    sx={{
                                      color: idx === 0 ? '#3b82f6' : '#10b981',
                                      width: 36,
                                      height: 36,
                                      border: '1px solid',
                                      borderColor: idx === 0 ? '#bfdbfe' : '#a7f3d0',
                                      borderRadius: 1,
                                      '&:hover': { bgcolor: idx === 0 ? '#eff6ff' : '#d1fae5' },
                                    }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          ) : linkInfo?.link ? (
                            <Tooltip title="開啟產品頁面">
                              <IconButton
                                onClick={() => window.open(linkInfo.link, '_blank', 'noopener,noreferrer')}
                                sx={{
                                  color: '#3b82f6',
                                  width: 36,
                                  height: 36,
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#eff6ff' },
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>

                      {/* Variants Breakdown */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {group.specs.map((spec, idx) => (
                          <Chip
                            key={idx}
                            onClick={() => {
                              setExpandedGroupOrderIds(prev => {
                                const next = new Set(prev);
                                spec.orders.forEach(o => {
                                  if (next.has(o)) next.delete(o);
                                  else next.add(o);
                                });
                                return next;
                              });
                            }}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span style={{ fontWeight: 600 }}>{spec.color}{spec.size !== '-' ? ` ${spec.size}` : ''}</span>
                                <span style={{ fontWeight: 700, color: '#3b82f6' }}>× {spec.totalQty}</span>
                                <span style={{ fontSize: '0.7rem', color: '#6366f1', marginLeft: 2 }}>
                                  ({spec.orders.map(o => `#${o}`).join(', ')})
                                </span>
                                {spec.orders.some(o => expandedGroupOrderIds.has(o))
                                  ? <ExpandLessIcon sx={{ fontSize: 14, color: '#6366f1', ml: 0.3 }} />
                                  : <ExpandMoreIcon sx={{ fontSize: 14, color: '#94a3b8', ml: 0.3 }} />}
                              </Box>
                            }
                            variant="outlined"
                            size="small"
                            sx={{
                              height: 'auto',
                              py: 0.5,
                              borderRadius: '8px',
                              borderColor: spec.orders.some(o => expandedGroupOrderIds.has(o)) ? '#6366f1' : '#e2e8f0',
                              bgcolor: spec.orders.some(o => expandedGroupOrderIds.has(o)) ? '#f5f3ff' : 'transparent',
                              cursor: 'pointer',
                              '&:hover': { borderColor: '#6366f1', bgcolor: '#f5f3ff' },
                              '& .MuiChip-label': { whiteSpace: 'normal', px: 1 },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Expanded Orders — show order summary when a variant chip is expanded */}
              {(() => {
                const expandedOrderIds = new Set<string>();
                group.specs.forEach(spec => {
                  spec.orders.forEach(o => {
                    if (expandedGroupOrderIds.has(o)) expandedOrderIds.add(o);
                  });
                });
                if (expandedOrderIds.size === 0) return null;

                return Array.from(expandedOrderIds).map(orderId => {
                  const order = allOrdersForGrouping.find(o => o.order_id === orderId);
                  if (!order) return null;
                  return (
                    <Box key={orderId} sx={{ ml: 4, mb: 2, borderLeft: '3px solid #6366f1', borderRadius: '0 12px 12px 0', p: 2, bgcolor: '#fafaf8' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Order {order.order_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.customer_name} · {moment(order.created_at).format('MM/DD HH:mm')} · {STATUS_LABELS[order.status] || order.status}{order.payment_method ? ` (${order.payment_method === 'credit_card' ? '信用卡' : order.payment_method === 'line_pay' ? 'LINE Pay' : order.payment_method === 'store_cod' ? '取貨付款' : order.payment_method})` : ''}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(order.order_items || []).map((item, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={`${item.title}${item.option1 ? ` ${item.option1}` : ''}${item.option2 ? ` ${item.option2}` : ''} ×${item.quantity}`}
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                });
              })()}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  // Helper: check if an item matches search query
  const itemMatchesSearch = useCallback((item: OrderItem, q: string, orderId: string) => {
    if (item.title?.toLowerCase().includes(q)) return true;
    if (item.sku?.toLowerCase().includes(q)) return true;
    if (item.option1?.toLowerCase().includes(q)) return true;
    if (item.option2?.toLowerCase().includes(q)) return true;
    if (item.variant_title?.toLowerCase().includes(q)) return true;
    const pk = getProductKey(item.sku || '', item.title, item.variant_title || '');
    const tag = productMetadata[pk]?.tag || '';
    if (tag.toLowerCase().includes(q)) return true;
    const key = `${orderId}__${item.title}__Default`;
    const t = tracking.get(key);
    if (t?.note?.toLowerCase().includes(q)) return true;
    return false;
  }, [getProductKey, productMetadata, tracking]);

  // Helper: check if an item matches tag filter
  const itemMatchesTag = useCallback((item: OrderItem) => {
    if (tagFilter === 'All') return true;
    const pk = getProductKey(item.sku || '', item.title, item.variant_title || '');
    const tag = productMetadata[pk]?.tag || '';
    if (tagFilter === 'NumbersOnly') return hasNumbersOnly(tag);
    if (tagFilter === 'NoNumbers') return !!tag && !hasNumbersOnly(tag);
    if (tagFilter === 'NoTag') return !tag || tag.trim() === '';
    return true;
  }, [tagFilter, getProductKey, productMetadata]);

  // Client-side filtering & sorting (same logic as Shopify — item-level filtering)
  const filteredOrders = useMemo(() => {
    let filtered: any[] = orders.map(o => ({
      ...o,
      order_items: [...(o.order_items || [])],
      _allItems: [...(o.order_items || [])],  // preserve original items for expand
      originalItemCount: (o.order_items || []).length,
      filteredItemCount: (o.order_items || []).length,
      isFiltered: false,
      isExpanded: false,
    }));

    // Country filter (order-level) — intl regions (see config/intlRegions.ts).
    // 'TW' = domestic (not intl); a region code = that intl country, with legacy
    // intl_hk rows (null shipping_country) resolving to 'HK'.
    if (countryFilter !== 'all') {
      filtered = countryFilter === 'TW'
        ? filtered.filter(o => !isIntlOrder(o))
        : filtered.filter(o => intlCountryOf(o) === countryFilter);
    }

    // Fulfillment filter (order-level)
    if (fulfillmentFilter !== 'all') {
      filtered = filtered.filter(o => {
        if (fulfillmentFilter === 'unfulfilled') return o.status !== 'SHIPPED' && o.status !== 'DELIVERED';
        if (fulfillmentFilter === 'fulfilled') return o.status === 'SHIPPED' || o.status === 'DELIVERED';
        return true;
      });
    }

    // Received filter (order-level, same as Shopify — keep ready-to-ship visible in "not-received")
    if (receivedFilter !== 'all') {
      filtered = filtered.filter(o => {
        const { allReceived, receivedCount, totalItems } = getOrderTrackingStats(o);
        const isReadyToShip = allReceived && totalItems > 0 && o.status !== 'SHIPPED' && o.status !== 'DELIVERED';
        if (receivedFilter === 'ready') return allReceived && totalItems > 0;
        if (receivedFilter === 'complete') return allReceived && totalItems > 0;
        if (receivedFilter === 'partial') return receivedCount > 0 && !allReceived;
        if (receivedFilter === 'pending' || receivedFilter === 'not-received') {
          if (isReadyToShip) return true;
          return receivedCount === 0;
        }
        return true;
      });
    }

    // Search filter (item-level — same as Shopify: show only matching items, unless order-level match or all received)
    if (searchFilter.trim()) {
      const q = searchFilter.toLowerCase().trim();
      filtered = filtered.map(o => {
        const isExpanded = expandedOrders.has(o.order_id);
        const { allReceived } = getOrderTrackingStats(o);

        // Order-level match → show all items
        const orderMatch =
          o.order_id?.toLowerCase().includes(q) ||
          o.customer_name?.toLowerCase().includes(q) ||
          o.customer_phone?.toLowerCase().includes(q) ||
          o.customer_email?.toLowerCase().includes(q);

        // Item-level match
        const matchingItems = (o._allItems || []).filter((item: OrderItem) => itemMatchesSearch(item, q, o.order_id));

        if (orderMatch || matchingItems.length > 0) {
          const shouldShowAll = orderMatch || isExpanded || allReceived;
          return {
            ...o,
            order_items: shouldShowAll ? o._allItems : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: matchingItems.length < o.originalItemCount,
            isExpanded: shouldShowAll,
            allItemsReceived: allReceived,
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Color filter (item-level — same as Shopify)
    if (colorFilter !== 'All') {
      filtered = filtered.map(o => {
        const matchingItems = (o._allItems || []).filter((item: OrderItem) => item.option1 === colorFilter);
        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(o.order_id);
          return {
            ...o,
            order_items: isExpanded ? o._allItems : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: matchingItems.length < o.originalItemCount,
            isExpanded,
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Size filter (item-level — same as Shopify)
    if (sizeFilter !== 'All') {
      filtered = filtered.map(o => {
        const matchingItems = (o._allItems || []).filter((item: OrderItem) => item.option2 === sizeFilter);
        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(o.order_id);
          return {
            ...o,
            order_items: isExpanded ? o._allItems : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: matchingItems.length < o.originalItemCount,
            isExpanded,
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Tag filter (item-level — same as Shopify)
    if (tagFilter !== 'All') {
      filtered = filtered.map(o => {
        const matchingItems = (o._allItems || []).filter((item: OrderItem) => itemMatchesTag(item));
        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(o.order_id);
          return {
            ...o,
            order_items: isExpanded ? o._allItems : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: matchingItems.length < o.originalItemCount,
            isExpanded,
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Compute allItemsReceived for each order
    filtered = filtered.map(o => {
      const { allReceived, receivedCount } = getOrderTrackingStats({ ...o, order_items: o._allItems });
      const isReadyToShip = allReceived && (o._allItems || []).length > 0 && o.status !== 'SHIPPED' && o.status !== 'DELIVERED';
      // Auto-expand if ready to ship and receivedFilter is 'not-received'
      const shouldAutoExpand = receivedFilter === 'not-received' && isReadyToShip;
      return {
        ...o,
        allItemsReceived: allReceived,
        receivedItemsInOrder: receivedCount,
        totalItemsInOrder: (o._allItems || []).length,
        order_items: shouldAutoExpand ? o._allItems : o.order_items,
        isFiltered: shouldAutoExpand ? false : o.isFiltered,
        isExpanded: shouldAutoExpand ? true : o.isExpanded,
      };
    });

    // Sort
    filtered.sort((a: any, b: any) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [orders, searchFilter, fulfillmentFilter, receivedFilter, countryFilter, colorFilter, sizeFilter, tagFilter, sortOrder, expandedOrders, getOrderTrackingStats, itemMatchesSearch, itemMatchesTag]);

  // Client-side pagination on filtered results
  const ORDERS_PER_PAGE = 50;
  const paginatedOrders = useMemo(() => {
    const start = (page - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, page]);

  const computedTotalPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  // ========== RENDER ==========
  return (
    <Box sx={S.page}>
      {/* Header */}
      <Box sx={S.header}>
        <Typography sx={S.headerTitle}>官網訂單管理</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* View Mode Toggle */}
          <Box sx={S.tabContainer}>
            <Button
              onClick={() => setViewMode('orders')}
              startIcon={<ViewListIcon sx={{ fontSize: 18 }} />}
              sx={S.tabButton(viewMode === 'orders')}
            >
              訂單
            </Button>
            <Button
              onClick={() => setViewMode('products')}
              startIcon={<CategoryIcon sx={{ fontSize: 18 }} />}
              sx={S.tabButton(viewMode === 'products')}
            >
              叫貨
            </Button>
          </Box>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
            onClick={() => {
              fetchOrders(); fetchTracking(); fetchProductMetadata();
              if (viewMode === 'products') fetchAllOrdersForGrouping();
            }} disabled={loading || loadingAllOrders} sx={S.headerBtn}>
            重新整理
          </Button>
        </Box>
      </Box>

      {/* 叫貨 mode — product grouping view */}
      {viewMode === 'products' && renderProductGroupingView()}

      {/* Orders mode — filter panel + order cards */}
      {viewMode === 'orders' && <>
      <Box sx={S.filterPanel}>
        <Box sx={{ mb: 2 }}>
          <TextField value={searchInput} label="搜尋訂單"
            placeholder="標籤編號、商品名稱、訂單號、規格或客戶名稱"
            variant="outlined" size="medium" fullWidth
            onChange={handleSearchChange}
            onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(); }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#8c8580' }} />,
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {(loading || isSearchPending) && <CircularProgress size={16} sx={{ mr: 1 }} />}
                  {searchInput && <IconButton size="small" onClick={clearSearch}><CloseIcon /></IconButton>}
                </Box>
              ),
            }}
            sx={S.searchField}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 140, ...S.filterSelect }}>
            <InputLabel id="sf-status-label">付款狀態</InputLabel>
            <Select labelId="sf-status-label" value={statusFilter} label="付款狀態"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="all">全部</MenuItem>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 140, ...S.filterSelect }}>
            <InputLabel id="sf-fulfillment-label">出貨狀態</InputLabel>
            <Select labelId="sf-fulfillment-label" value={fulfillmentFilter} label="出貨狀態"
              onChange={(e) => setFulfillmentFilter(e.target.value)}>
              <MenuItem value="all">全部訂單</MenuItem>
              <MenuItem value="unfulfilled">未出貨</MenuItem>
              <MenuItem value="fulfilled">已出貨</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="sf-color-label">顏色</InputLabel>
            <Select labelId="sf-color-label" value={colorFilter} label="顏色"
              onChange={(e) => setColorFilter(e.target.value)}>
              <MenuItem value="All">全部</MenuItem>
              {getUniqueColorsAndSizes.colors.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 110, ...S.filterSelect }}>
            <InputLabel id="sf-size-label">尺寸</InputLabel>
            <Select labelId="sf-size-label" value={sizeFilter} label="尺寸"
              onChange={(e) => setSizeFilter(e.target.value)}>
              <MenuItem value="All">全部</MenuItem>
              {getUniqueColorsAndSizes.sizes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="sf-tag-label">標籤</InputLabel>
            <Select labelId="sf-tag-label" value={tagFilter} label="標籤"
              onChange={(e) => setTagFilter(e.target.value)}>
              <MenuItem value="All">所有</MenuItem>
              <MenuItem value="NumbersOnly">僅數字</MenuItem>
              <MenuItem value="NoNumbers">非數字</MenuItem>
              <MenuItem value="NoTag">無標籤</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="sf-sort-label">排序</InputLabel>
            <Select labelId="sf-sort-label" value={sortOrder} label="排序"
              onChange={(e) => setSortOrder(e.target.value)}
              startAdornment={<SortIcon sx={{ mr: 1, color: '#8c8580' }} />}>
              <MenuItem value="newest">最新優先</MenuItem>
              <MenuItem value="oldest">最舊優先</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130, ...S.filterSelect }}>
            <InputLabel id="sf-received-label">收貨狀態</InputLabel>
            <Select labelId="sf-received-label" value={receivedFilter} label="收貨狀態"
              onChange={(e) => setReceivedFilter(e.target.value)}>
              <MenuItem value="all">全部</MenuItem>
              <MenuItem value="ready">可出貨</MenuItem>
              <MenuItem value="complete">已收齊</MenuItem>
              <MenuItem value="not-received">未收到</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="sf-country-label">配送地區</InputLabel>
            <Select labelId="sf-country-label" value={countryFilter} label="配送地區"
              onChange={(e) => setCountryFilter(e.target.value)}>
              <MenuItem value="all">全部</MenuItem>
              <MenuItem value="TW">台灣</MenuItem>
              {INTL_REGION_LIST.map(r => (
                <MenuItem key={r.code} value={r.code}>{r.flag} {r.nameZh}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {hasActiveFilters && (
            <Button variant="outlined" size="small" onClick={clearAllFilters}
              startIcon={<CloseIcon />} sx={{ ...S.headerBtn, height: 36 }}>
              清除
            </Button>
          )}

          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#5c5854', ml: 'auto' }}>
            共 {filteredOrders.length} / {totalOrders} 筆訂單
          </Typography>
        </Box>
      </Box>

      {/* Summary Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2, mx: { xs: 2, md: 3 } }}>
        {[
          { label: '待處理', count: stats.pending, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
          { label: '部分收到', count: stats.partial, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          { label: '可出貨', count: stats.ready, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: '已出貨', count: stats.shipped, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
        ].map(s => (
          <Box key={s.label} sx={{ px: 2, py: 1.5, bgcolor: s.bg, borderRadius: '12px', border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', gap: 1.5, transition: 'transform 0.15s ease', '&:hover': { transform: 'translateY(-1px)' } }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: '"DM Sans", sans-serif' }}>{s.count}</Typography>
            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: s.color, lineHeight: 1.2, letterSpacing: '0.02em' }}>{s.label}</Typography>
              <Typography sx={{ fontSize: '0.65rem', color: s.color, opacity: 0.6, lineHeight: 1.2, mt: 0.25 }}>
                {stats.total > 0 ? `${Math.round((s.count / stats.total) * 100)}%` : '0%'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Loading */}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {/* Empty state */}
      {!loading && filteredOrders.length === 0 && (
        <Alert severity="info" sx={{ mx: { xs: 2, md: 3 }, mt: 2, borderRadius: '12px' }}>沒有找到官網訂單</Alert>
      )}

      {/* ===== ORDER CARDS (always expanded, same as Shopify) ===== */}
      <Box sx={{ mx: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!loading && paginatedOrders.map(order => {
          // Use _allItems (original unfiltered items) for tracking stats to avoid
          // showing "ready to ship" when only filtered items are received
          const allItemsOrder = (order as any)._allItems
            ? { ...order, order_items: (order as any)._allItems }
            : order;
          const { receivedCount, totalItems, allReceived, hasAnyTrackingData } = getOrderTrackingStats(allItemsOrder);
          const isShipped = order.status === 'SHIPPED' || order.status === 'DELIVERED';
          // For shipped orders with no tracking data, treat as "no tracking info" (pre-tracking-feature orders)
          const noTrackingForShipped = isShipped && !hasAnyTrackingData;
          const cardStatus = getCardStatus(allReceived, receivedCount, totalItems, order.status);
          const receivedStatus = allReceived && totalItems > 0 ? 'complete' : receivedCount > 0 ? 'partial' : 'pending';
          const isReadyToShip = allReceived && totalItems > 0 && !isShipped;
          const hasTrackingInfo = !!(order.tracking_number || order.cvs_payment_no);
          const hasOutOfStock = ((order as any)._allItems || order.order_items)?.some((item: any) => {
            const key = trackingKey(order.order_id, item.title, getItemVariant(item));
            return tracking.get(key)?.outOfStock;
          }) || false;
          const showGallery = galleryOrders.has(order.order_id);

          return (
            <Card key={order.order_id} sx={{ ...S.orderCard(cardStatus), mb: 0, width: '100%' }}>

              {/* ===== CARD HEADER (title + subheader + action buttons) ===== */}
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, ...S.mono, fontSize: '1.05rem', color: '#1a1a1a' }}>
                      Order {order.order_id}
                    </Typography>

                    {/* International shipping badge — intl regions (config/intlRegions.ts) */}
                    {isIntlOrder(order) && (
                      <Chip
                        label={`${intlRegionOf(order)?.flag ?? '🌏'} 國際配送 · ${intlRegionOf(order)?.nameZh ?? ''}`}
                        size="small"
                        sx={{
                          ...S.statusChip,
                          backgroundColor: '#fef3c7',
                          color: '#92400e',
                          fontWeight: 700,
                          border: '1px solid #fde68a',
                        }}
                      />
                    )}

                    {/* Ready to ship */}
                    {isReadyToShip && (
                      <Chip icon={<LocalShippingIcon />} label="可出貨 ✓" color="success" size="small" sx={S.statusChip} />
                    )}
                    {/* Shipped with no tracking data — pre-tracking-feature order */}
                    {noTrackingForShipped && (
                      <Chip label="無收貨紀錄" size="small" sx={{ ...S.statusChip, backgroundColor: '#e0e0e0', color: '#666' }} />
                    )}
                    {/* Received status chips — only show if tracking data exists */}
                    {!noTrackingForShipped && receivedStatus === 'complete' && !isReadyToShip && (
                      <Chip icon={<CheckCircleIcon />} label="已收齊" color="success" size="small" sx={S.statusChip} />
                    )}
                    {!noTrackingForShipped && receivedStatus === 'partial' && (
                      <Chip icon={<WarningIcon />} label={`已收 ${receivedCount}/${totalItems}`} color="warning" size="small" sx={S.statusChip} />
                    )}
                    {!noTrackingForShipped && receivedStatus === 'pending' && totalItems > 0 && (
                      <Chip icon={<ErrorIcon />} label="未收到" color="error" size="small" sx={S.statusChip} />
                    )}

                    {/* Progress bar — hide for shipped orders with no tracking data */}
                    {totalItems > 0 && !noTrackingForShipped && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
                        <LinearProgress variant="determinate"
                          value={(receivedCount / totalItems) * 100}
                          color={allReceived ? 'success' : receivedStatus === 'partial' ? 'warning' : 'error'}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                          {receivedCount}/{totalItems}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
                subheader={
                  <Grid container spacing={2} sx={{ color: '#5c5854', fontSize: '0.875rem' }}>
                    <Grid item xs={12} md={4}>
                      訂單日期: {moment(order.created_at).format('YYYY-MM-DD HH:mm')}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      Status: {STATUS_LABELS[order.status] || order.status}
                      {order.payment_method && (
                        <Typography component="span" sx={{ ml: 1, color: '#888', fontSize: '0.8rem' }}>
                          ({order.payment_method === 'credit_card' ? '信用卡' : order.payment_method === 'line_pay' ? 'LINE Pay' : order.payment_method === 'store_cod' ? '取貨付款' : order.payment_method})
                        </Typography>
                      )}
                      {isReadyToShip && (
                        <Typography component="span" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                          → READY TO SHIP
                        </Typography>
                      )}
                    </Grid>
                    {/* Tracking number */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">追蹤號碼 (Tracking Number)</Typography>
                      {(() => {
                        const tn = getDisplayTrackingNumber(order);
                        return tn ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body1" sx={{ mr: 1, ...S.mono }}>{tn}</Typography>
                            <IconButton size="small" onClick={() => copyToClipboard(tn)} title="複製追蹤碼">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <Typography variant="body1" color="text.secondary">尚未出貨</Typography>
                        );
                      })()}
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2"><strong>商品數量:</strong> {totalItems}</Typography>
                        <Typography variant="body2"><strong>已收到:</strong> {receivedCount}/{totalItems}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      Customer: {order.customer_name} · {order.customer_phone}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>地址:</Typography>
                        {order.delivery_method === 'pickup' ? (
                          <>
                            <Chip label="超商" size="small" sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }} />
                            <Typography variant="body2" sx={{ ...S.mono, fontSize: '0.85rem' }}>
                              {[order.store_id, order.store_name, order.store_address].filter(Boolean).join(' · ')}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <Chip label="🚛 宅配" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, border: '1px solid #fde68a' }} />
                            {(order.shipping_address || order.store_address) && (
                              <Typography variant="body2" sx={{ ...S.mono, fontSize: '0.85rem' }}>
                                {order.shipping_address || order.store_address}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                }
                action={
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center',
                    '@media (max-width: 900px)': { width: '100%', justifyContent: 'flex-start', mt: 2 } }}>

                    {/* 顯示圖庫 button */}
                    <Button variant="outlined" color="primary" size="small"
                      startIcon={showGallery ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      onClick={() => setGalleryOrders(prev => {
                        const next = new Set(prev);
                        if (next.has(order.order_id)) next.delete(order.order_id); else next.add(order.order_id);
                        return next;
                      })}
                      sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, transition: 'all 0.15s ease', borderColor: '#e8e5e1', color: '#5c5854', '&:hover': { borderColor: '#d1cdc8', bgcolor: 'rgba(0,0,0,0.02)' } }}>
                      {showGallery ? '隱藏圖庫' : '顯示圖庫'}
                    </Button>

                    {/* Bulk open product links */}
                    {(() => {
                      const itemsWithLinks = order.order_items?.filter((it: OrderItem) => it.sku && productLinksBySku.has(it.sku)) || [];
                      if (itemsWithLinks.length === 0) return null;
                      return (
                        <Button variant="outlined" size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => {
                            itemsWithLinks.forEach((it: OrderItem, i: number) => {
                              const info = productLinksBySku.get(it.sku!);
                              const link = info?.allLinks?.[0] || info?.link;
                              if (link) setTimeout(() => window.open(link, '_blank', 'noopener,noreferrer'), i * 100);
                            });
                            enqueueSnackbar(`開啟 ${itemsWithLinks.length} 個連結`, { variant: 'info' });
                          }}
                          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: '#e8e5e1', color: '#5c5854', '&:hover': { borderColor: '#d1cdc8', bgcolor: 'rgba(0,0,0,0.02)' } }}>
                          連結 ({itemsWithLinks.length})
                        </Button>
                      );
                    })()}

                    {/* Print shipping label (only if has tracking info and is pickup) */}
                    {hasTrackingInfo && order.delivery_method === 'pickup' && (
                      <IconButton size="small" color="secondary" title="列印出貨單"
                        onClick={() => handlePrintShippingDocument(order)}>
                        <PrintIcon />
                      </IconButton>
                    )}

                    {/* 已建立物流單 chip */}
                    {hasTrackingInfo && (
                      <Chip icon={<LocalShippingIcon />} label="已建立物流單" color="info" size="small" sx={{ ml: 1 }} />
                    )}

                    {/* 已出貨 button */}
                    <Tooltip title={!allReceived ? "All items must be received before fulfillment" : "Mark order as shipped"} arrow>
                      <span>
                        <Button variant="outlined" color="primary" size="small"
                          startIcon={<LocalShippingIcon />}
                          disabled={!allReceived}
                          onClick={() => handleStatusChange(order.order_id, 'SHIPPED')}
                        >
                          已出貨
                        </Button>
                      </span>
                    </Tooltip>

                    {/* 快速出貨 for convenience store without tracking */}
                    {!hasTrackingInfo && order.delivery_method === 'pickup' && order.store_id && (
                      <Tooltip title={!allReceived ? "All items must be received before quick shipping" : "使用預設資訊快速建立物流單"} arrow>
                        <span>
                          <Button variant="contained" color="success" size="small"
                            startIcon={<LocalShippingIcon />}
                            disabled={!allReceived}
                            onClick={() => handleQuickShip(order)}>
                            快速出貨
                          </Button>
                        </span>
                      </Tooltip>
                    )}

                    {/* 超商出貨 / 宅配出貨 — creates ECPay logistics (hidden for intl orders — SF handles international) */}
                    {!isIntlOrder(order) && (
                      <Button variant="contained" size="small"
                        color={order.delivery_method !== 'pickup' ? 'warning' : 'primary'}
                        startIcon={<LocalShippingIcon />}
                        onClick={() => {
                          if (order.delivery_method !== 'pickup') {
                            handleHsinchuShipping(order);
                          } else {
                            setShippingDialogData(buildShippingOrder(order));
                          }
                        }}>
                        {order.delivery_method !== 'pickup' ? '宅配出貨' : '超商出貨'}
                      </Button>
                    )}

                    {/* SF International — manual create when auto-create failed.
                        SF-carried regions only: /sf/create rejects Parxl regions
                        (SG), which are booked in the Parxl console instead. */}
                    {isSfIntlOrder(order) && !order.sf_waybill_no && (
                      <Tooltip title={order.sf_last_error ? `上次失敗：${order.sf_last_error}` : '建立順豐國際單'} arrow>
                        <Button variant="contained" size="small" color="warning"
                          startIcon={<LocalShippingIcon />}
                          onClick={async () => {
                            if (!window.confirm(`建立 ${order.order_id} 的順豐國際單？`)) return;
                            try {
                              const resp = await api.post(`/api/storefront/orders/${order.order_id}/sf/create`);
                              const { sfWaybillNo, labelUrl, invoiceUrl, customerOrderNo } = resp.data || {};
                              setOrders(prev => prev.map(o => o.order_id === order.order_id ? {
                                ...o,
                                sf_waybill_no: sfWaybillNo,
                                sf_label_url: labelUrl,
                                sf_invoice_url: invoiceUrl,
                                sf_customer_order_no: customerOrderNo,
                                sf_last_error: null,
                              } : o));
                              enqueueSnackbar(`順豐單號：${sfWaybillNo}`, { variant: 'success' });
                            } catch (err: any) {
                              const msg = err?.response?.data?.error || err.message;
                              enqueueSnackbar(`建單失敗：${msg}`, { variant: 'error' });
                            }
                          }}>
                          建立順豐單
                        </Button>
                      </Tooltip>
                    )}

                    {/* SF International — already created: show waybill + actions */}
                    {isIntlOrder(order) && order.sf_waybill_no && (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          icon={<LocalShippingIcon />}
                          label={`SF ${order.sf_waybill_no}`}
                          color={order.sf_cancelled_at ? 'error' : 'info'}
                          size="small"
                          onClick={() => copyToClipboard(order.sf_waybill_no!)}
                          sx={{ fontWeight: 600, cursor: 'pointer' }}
                        />
                        {order.sf_label_url && (
                          <Tooltip title="列印面單" arrow>
                            <IconButton size="small" color="primary"
                              onClick={() => window.open(order.sf_label_url!, '_blank', 'noopener,noreferrer')}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {order.sf_invoice_url && (
                          <Tooltip title="列印 Commercial Invoice" arrow>
                            <Button variant="outlined" size="small"
                              onClick={() => window.open(order.sf_invoice_url!, '_blank', 'noopener,noreferrer')}
                              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1 }}>
                              Invoice
                            </Button>
                          </Tooltip>
                        )}
                        {!order.sf_cancelled_at && (
                          <Button variant="outlined" size="small" color="error"
                            onClick={async () => {
                              if (!window.confirm(`取消順豐單 ${order.sf_waybill_no}？`)) return;
                              try {
                                await api.post(`/api/storefront/orders/${order.order_id}/sf/cancel`);
                                setOrders(prev => prev.map(o => o.order_id === order.order_id ? {
                                  ...o, sf_cancelled_at: new Date().toISOString(),
                                } : o));
                                enqueueSnackbar('順豐單已取消', { variant: 'success' });
                              } catch (err: any) {
                                const msg = err?.response?.data?.error || err.message;
                                enqueueSnackbar(`取消失敗：${msg}`, { variant: 'error' });
                              }
                            }}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                            取消順豐單
                          </Button>
                        )}
                        {order.sf_cancelled_at && (
                          <Chip label="順豐單已取消" size="small" color="error" />
                        )}
                      </Box>
                    )}

                    {/* 退刷 */}
                    {(order.payment_method === 'credit_card' || order.payment_method === 'line_pay') && order.payment_status !== 'REFUNDED' && order.status !== 'CANCELLED' && (order.payment_status === 'SUCCESS' || order.payment_method === 'line_pay') && (
                      <Button variant="outlined" color="warning" size="small"
                        onClick={() => openRefundDialog(
                          order.order_id,
                          order.payment_trade_no || '',
                          Math.floor(Number(order.total_amount) + Number(order.shipping_fee || 0)),
                          order.payment_method
                        )}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        退刷
                      </Button>
                    )}
                    {order.payment_status === 'REFUNDED' && (
                      <Chip label="已退刷" color="warning" size="small" />
                    )}

                    {/* 取消訂單 */}
                    {order.status !== 'CANCELLED' && order.status !== 'SHIPPED' && order.status !== 'DELIVERED' && (
                      <Button variant="outlined" color="error" size="small"
                        onClick={async () => {
                          if (!window.confirm(`確定要取消訂單 ${order.order_id}？`)) return;
                          try {
                            await api.post(`/api/storefront/orders/${order.order_id}/cancel`);
                            setOrders(prev => prev.map(o => o.order_id === order.order_id ? { ...o, status: 'CANCELLED' } : o));
                            enqueueSnackbar(`訂單 ${order.order_id} 已取消`, { variant: 'success' });
                          } catch (err: any) {
                            enqueueSnackbar(`取消失敗: ${err.message}`, { variant: 'error' });
                          }
                        }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        取消訂單
                      </Button>
                    )}
                    {order.status === 'CANCELLED' && (
                      <Chip label="已取消" color="error" size="small" />
                    )}

                    {/* 補寄 — re-ship for convenience store orders (any status with store_id) */}
                    {order.delivery_method === 'pickup' && order.store_id && order.status !== 'CANCELLED' && (
                      <Button variant="outlined" color="info" size="small"
                        startIcon={<LocalShippingIcon />}
                        onClick={() => {
                          setReshipOrder(order);
                          setReshipPaymentType('N');
                          setReshipCodAmount(String(Math.floor(order.total_amount || 0)));
                          setReshipDialogOpen(true);
                        }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        補寄
                      </Button>
                    )}

                    {/* 退回未出貨 — revert SHIPPED back */}
                    {order.status === 'SHIPPED' && (
                      <Button variant="outlined" color="secondary" size="small"
                        onClick={() => handleRevertShipped(order)}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        退回未出貨
                      </Button>
                    )}

                    {/* 寄斷貨通知 */}
                    {hasOutOfStock && order.status !== 'CANCELLED' && (
                      <Button variant="outlined" color="warning" size="small"
                        startIcon={<EmailIcon />}
                        onClick={async () => {
                          if (!window.confirm(`確定要寄送斷貨通知給 ${order.customer_name}？\n系統將自動扣除斷貨商品金額並通知客戶。`)) return;
                          try {
                            const res = await api.post(`/api/storefront/orders/${order.order_id}/out-of-stock-notify`);
                            if (res.data.success) {
                              setOrders(prev => prev.map(o => o.order_id === order.order_id ? { ...o, total_amount: res.data.remainingTotal } : o));
                              enqueueSnackbar(`斷貨通知已寄出，訂單金額已更新`, { variant: 'success' });
                            } else {
                              enqueueSnackbar(`寄送失敗: ${res.data.error}`, { variant: 'error' });
                            }
                          } catch (err: any) {
                            enqueueSnackbar(`寄送失敗: ${err.response?.data?.error || err.message}`, { variant: 'error' });
                          }
                        }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        寄斷貨通知
                      </Button>
                    )}
                  </Box>
                }
              />

              <Divider />

              {/* PROMINENT READY TO SHIP BANNER */}
              {isReadyToShip && (
                <Alert severity="success" icon={<LocalShippingIcon />}
                  sx={{
                    m: 2, border: '1.5px solid #22c55e', borderRadius: '12px',
                    animation: 'pulseGlow 2s ease-in-out 3',
                    '@keyframes pulseGlow': { '0%, 100%': { boxShadow: 'none' }, '50%': { boxShadow: '0 0 16px rgba(34,197,94,0.3)' } },
                  }}>
                  <Typography variant="body1" fontWeight="bold">✅ 全部商品已收到！可以出貨</Typography>
                </Alert>
              )}

              {/* Filter warning — show when items are filtered within this order (same as Shopify) */}
              {(order as any).isFiltered && (() => {
                const totalItems = (order as any).originalItemCount || 0;
                const receivedItems = (order as any).receivedItemsInOrder || 0;
                return (
                  <Alert severity="warning" icon={<WarningIcon />} sx={{ m: 2 }}
                    action={
                      <Button color="inherit" size="small"
                        onClick={() => {
                          setExpandedOrders(prev => {
                            const next = new Set(prev);
                            if (next.has(order.order_id)) next.delete(order.order_id);
                            else next.add(order.order_id);
                            return next;
                          });
                        }}>
                        {(order as any).isExpanded ? '僅顯示搜尋結果' : '顯示全部商品'}
                      </Button>
                    }>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        搜尋結果僅顯示 {(order as any).filteredItemCount}/{totalItems} 件商品
                      </Typography>
                      <Typography variant="body2">
                        此訂單共 {totalItems} 件商品，已收到 {receivedItems}/{totalItems} 件
                      </Typography>
                      {(order as any).allItemsReceived && (
                        <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ mt: 0.5 }}>
                          ✅ 全部商品已收到，可以出貨!
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                );
              })()}

              {/* ===== ITEM TABLE (same columns as Shopify: 商品, 規格, 數量, 標籤, 備註, 已收到, 參考圖, 品號, 訂購連結) ===== */}
              <CardContent sx={{ padding: '16px', overflow: 'visible', '&:last-child': { paddingBottom: '16px' } }}>
                <TableContainer sx={{ width: '100%', overflowX: 'auto', overflowY: 'visible', maxHeight: 'none',
                  WebkitOverflowScrolling: 'touch', '@media (max-width: 900px)': { overflowX: 'scroll' } }}>
                  <Table size="small" sx={{ minWidth: { xs: 1200, md: 1200 }, tableLayout: 'fixed', '@media (max-width: 600px)': { minWidth: 1000 } }}>
                    <TableHead sx={S.tableHeader}>
                      <TableRow>
                        <TableCell width="60px" sx={{ minWidth: '60px' }}>圖片</TableCell>
                        <TableCell width="200px" sx={{ minWidth: '200px' }}>商品</TableCell>
                        <TableCell width="120px" sx={{ minWidth: '120px' }}>規格</TableCell>
                        <TableCell width="80px" sx={{ minWidth: '80px' }}>數量</TableCell>
                        <TableCell width="120px" sx={{ minWidth: '120px' }}>標籤</TableCell>
                        <TableCell width="200px" sx={{ minWidth: '200px' }}>備註</TableCell>
                        <TableCell width="100px" sx={{ minWidth: '100px' }}>已收到</TableCell>
                        <TableCell width="80px" sx={{ minWidth: '80px' }}>斷貨</TableCell>
                        <TableCell width="150px" sx={{ minWidth: '150px' }}>參考圖</TableCell>
                        <TableCell width="120px" sx={{ minWidth: '120px' }}>品號</TableCell>
                        <TableCell width="100px" sx={{ minWidth: '100px' }}>訂購連結</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.order_items?.map((item: OrderItem) => {
                        const itemVariant = getItemVariant(item);
                        const key = trackingKey(order.order_id, item.title, itemVariant);
                        const t = tracking.get(key);
                        const received = t?.received || false;
                        const outOfStock = t?.outOfStock || false;
                        const note = t?.note || '';
                        const productKey = getProductKey(item.sku || '', item.title, item.variant_title || '');
                        const metadata = productMetadata[productKey];
                        const tag = metadata?.tag || '';
                        const referenceImage = metadata?.reference_image || t?.referenceImage || '';

                        return (
                          <TableRow key={item.id} sx={S.tableRow(received)}>
                            {/* 圖片 (hover to zoom) */}
                            <TableCell sx={{ p: 1 }}>
                              {item.image_url ? (
                                <HoverZoomImage src={item.image_url} alt={item.title} width={60} height={60} />
                              ) : (
                                <Box sx={{ width: 60, height: 60, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Typography variant="caption" color="text.secondary">—</Typography>
                                </Box>
                              )}
                            </TableCell>

                            {/* 商品 */}
                            <TableCell>
                              <Typography variant="body2">
                                {item.title}
                                {received && <CheckCircleIcon sx={{ ml: 1, color: 'success.main', fontSize: 16 }} />}
                              </Typography>
                            </TableCell>

                            {/* 規格 (variant: color / size — editable) */}
                            <TableCell>
                              <ExcelLikeField
                                value={item.variant_title && item.variant_title !== 'Default Title'
                                  ? item.variant_title
                                  : [item.option1, item.option2].filter(Boolean).join(' / ') || '-'}
                                onSave={(v) => handleVariantUpdate(order.order_id, item, v)}
                                placeholder="—"
                              />
                            </TableCell>

                            {/* 數量 */}
                            <TableCell sx={item.quantity > 1 ? { bgcolor: '#fef3c7', fontWeight: 'bold', ...S.mono } : {}}>
                              {item.quantity}
                            </TableCell>

                            {/* 標籤 */}
                            <TableCell>
                              <ExcelLikeField value={tag}
                                onSave={(v) => handleTagUpdate(item, v)}
                                placeholder="Add tag..." />
                            </TableCell>

                            {/* 備註 */}
                            <TableCell>
                              <ExcelLikeField value={note}
                                onSave={(v) => handleTrackingUpdate(order.order_id, item.title, itemVariant, 'note', v)}
                                placeholder="Add note..." multiline rows={2} />
                            </TableCell>

                            {/* 已收到 */}
                            <TableCell>
                              <Tooltip title={received && t?.receivedBy
                                ? `Received by: ${t.receivedBy}${t.receivedAt ? `\nAt: ${new Date(t.receivedAt).toLocaleString()}` : ''}`
                                : "Mark as received"} arrow placement="top">
                                <Checkbox checked={received}
                                  onChange={(e) => handleTrackingUpdate(order.order_id, item.title, itemVariant, 'received', e.target.checked)}
                                  sx={{ '&:hover': { transform: 'scale(1.05)' }, transition: 'transform 0.15s ease' }} />
                              </Tooltip>
                            </TableCell>

                            {/* 斷貨 */}
                            <TableCell>
                              <Tooltip title={outOfStock ? '此商品已斷貨' : '標記為斷貨'} arrow placement="top">
                                <Checkbox checked={outOfStock}
                                  onChange={(e) => handleTrackingUpdate(order.order_id, item.title, itemVariant, 'outOfStock', e.target.checked)}
                                  sx={{
                                    color: outOfStock ? '#e65100' : undefined,
                                    '&.Mui-checked': { color: '#e65100' },
                                    '&:hover': { transform: 'scale(1.05)' }, transition: 'transform 0.15s ease',
                                  }} />
                              </Tooltip>
                            </TableCell>

                            {/* 參考圖 */}
                            <TableCell sx={{ p: 1 }}>
                              {referenceImage ? (
                                <Box sx={{ position: 'relative', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img src={referenceImage} alt="Reference" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                                  <IconButton size="small"
                                    sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: 'white', zIndex: 10, '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', border: '1px solid #ddd' }}
                                    onClick={() => handleRemoveReferenceImage(order.order_id, item.title, itemVariant)}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Button component="label" variant="outlined" size="small" sx={{ fontSize: '0.7rem', p: 0.5 }}>
                                  UPLOAD
                                  <input type="file" hidden accept="image/*"
                                    onChange={(e) => handleReferenceImageUpload(e, order.order_id, item.title, itemVariant)} />
                                </Button>
                              )}
                            </TableCell>

                            {/* 品號 (SKU) — editable */}
                            <TableCell>
                              <ExcelLikeField
                                value={item.sku || ''}
                                onSave={(v) => handleSkuUpdate(order.order_id, item, v)}
                                placeholder="SKU..."
                              />
                            </TableCell>

                            {/* 訂購連結 */}
                            <TableCell>
                              {(() => {
                                const linkInfo = item.sku ? productLinksBySku.get(item.sku) : undefined;
                                if (linkInfo?.allLinks && linkInfo.allLinks.length > 0) {
                                  const firstLink = linkInfo.allLinks[0];
                                  const secondLink = linkInfo.allLinks.length > 1 ? linkInfo.allLinks[1] : null;
                                  return (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      {firstLink && (
                                        <Tooltip title="開啟第一個連結">
                                          <IconButton
                                            onClick={() => window.open(firstLink, '_blank', 'noopener,noreferrer')}
                                            sx={{ color: '#3b82f6', width: 36, height: 36, border: '1px solid #bfdbfe', borderRadius: 1, '&:hover': { bgcolor: '#eff6ff' } }}
                                          >
                                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      {secondLink && (
                                        <Tooltip title="開啟第二個連結">
                                          <IconButton
                                            onClick={() => window.open(secondLink, '_blank', 'noopener,noreferrer')}
                                            sx={{ color: '#10b981', width: 36, height: 36, border: '1px solid #a7f3d0', borderRadius: 1, '&:hover': { bgcolor: '#d1fae5' } }}
                                          >
                                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  );
                                } else if (linkInfo?.link) {
                                  return (
                                    <Tooltip title="開啟產品頁面">
                                      <IconButton
                                        onClick={() => window.open(linkInfo.link, '_blank', 'noopener,noreferrer')}
                                        sx={{ color: '#3b82f6', width: 36, height: 36, border: '1px solid #bfdbfe', borderRadius: 1, '&:hover': { bgcolor: '#eff6ff' } }}
                                      >
                                        <OpenInNewIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  );
                                }
                                return <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>-</Typography>;
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Product Image Gallery (same component as Shopify order page) */}
                {showGallery && (
                  <Box sx={{ p: 2, bgcolor: '#fafaf8', borderTop: '1px solid #e8e5e1' }}>
                    <ProductImageGallery
                      order={{
                        orderId: order.order_id,
                        items: order.order_items?.map((item: OrderItem) => ({
                          id: item.id,
                          productName: item.title,
                          itemSpec: getItemVariant(item),
                          quantity: item.quantity,
                          sku: item.sku || '',
                          imageUrl: item.image_url || '',
                          original: {
                            all_images: Array.isArray(item.all_images)
                              ? JSON.stringify(item.all_images)
                              : typeof item.all_images === 'string' ? item.all_images : '',
                          },
                        })) || [],
                      }}
                    />
                  </Box>
                )}

                {/* Order summary when all received */}
                {isReadyToShip && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.03)', borderRadius: '10px' }}>
                    <Typography variant="body2" color="text.secondary">
                      📊 <strong>Order Summary:</strong> {receivedCount}/{totalItems} items received
                      <Typography component="span" sx={{ ml: 2, color: 'success.main', fontWeight: 'bold' }}>
                        ✅ All items received - Ready to ship!
                      </Typography>
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Pagination */}
      {computedTotalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination count={computedTotalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
        </Stack>
      )}
      </>}

      {/* Status change menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)}
        onClose={() => { setStatusMenuAnchor(null); setStatusMenuOrderId(''); }}>
        {STATUS_OPTIONS.map(s => (
          <MenuItem key={s} onClick={() => handleStatusChange(statusMenuOrderId, s)}>
            {STATUS_LABELS[s] || s}
          </MenuItem>
        ))}
      </Menu>

      {/* Convenience Store Shipping Modal */}
      {shippingDialogData && (
        <ShippingPreparationModal
          open={!!shippingDialogData}
          onClose={() => setShippingDialogData(null)}
          order={shippingDialogData}
          enqueueSnackbar={enqueueSnackbar}
          onShippingPrepared={async (preparedData) => {
            setShippingDialogData(null);

            let displayTN = '';
            if (preparedData.trackingNumber) displayTN = preparedData.trackingNumber;
            else if (preparedData.paymentNo) {
              const lt = preparedData.logisticsSubType || 'UNIMARTC2C';
              displayTN = lt === 'FAMIC2C' ? preparedData.paymentNo
                : preparedData.validationNo ? `${preparedData.paymentNo}${preparedData.validationNo}` : preparedData.paymentNo;
            }

            // Update order status to SHIPPED (include tracking number for email)
            const dialogTN = preparedData.trackingNumber
              || (preparedData.validationNo ? `${preparedData.paymentNo}${preparedData.validationNo}` : preparedData.paymentNo);
            try {
              await api.patch(`/api/storefront/orders/${shippingDialogData.orderId}/status`, { status: 'SHIPPED', trackingNumber: dialogTN });
            } catch (statusErr) {
              console.warn('Failed to update order status to SHIPPED:', statusErr);
            }

            setOrders(prev => prev.map(o => {
              if (o.order_id === shippingDialogData.orderId) {
                return {
                  ...o,
                  status: 'SHIPPED',
                  tracking_number: preparedData.trackingNumber || null,
                  cvs_payment_no: preparedData.paymentNo || null,
                  cvs_validation_no: preparedData.validationNo || null,
                  ecpay_logistics_id: preparedData.logisticsId || null,
                  elo_logistics_sub_type: preparedData.logisticsSubType || o.elo_logistics_sub_type,
                };
              }
              return o;
            }));

            enqueueSnackbar(`超商出貨成功！追蹤號碼: ${displayTN}`, { variant: 'success', autoHideDuration: 6000 });
          }}
        />
      )}

      {/* 補寄 Dialog */}
      <Dialog open={reshipDialogOpen} onClose={() => { setReshipDialogOpen(false); setReshipOrder(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>補寄</DialogTitle>
        <DialogContent>
          {reshipOrder && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                訂單編號: {reshipOrder.order_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                客戶: {reshipOrder.customer_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                門市: {reshipOrder.store_name || reshipOrder.store_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                金額: NT${Math.floor(reshipOrder.total_amount || 0).toLocaleString()}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>付款方式</Typography>
              <RadioGroup value={reshipPaymentType} onChange={(e) => setReshipPaymentType(e.target.value as 'Y' | 'N')}>
                <FormControlLabel value="N" control={<Radio size="small" />} label="取貨不用付款（已線上付款）" />
                <FormControlLabel value="Y" control={<Radio size="small" />} label="貨到付款" />
              </RadioGroup>
              {reshipPaymentType === 'Y' && (
                <TextField
                  fullWidth size="small" type="number" label="貨到付款金額 (NT$)"
                  value={reshipCodAmount}
                  onChange={(e) => setReshipCodAmount(e.target.value)}
                  sx={{ mt: 2 }}
                  inputProps={{ min: 1 }}
                  helperText={`訂單原金額: NT$${Math.floor(reshipOrder.total_amount || 0).toLocaleString()}`}
                />
              )}
              <Alert severity="info" sx={{ mt: 2 }}>
                系統將自動產生新的寄件編號（如 {reshipOrder.order_id}-1）以避免重複
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReshipDialogOpen(false); setReshipOrder(null); }}>取消</Button>
          <Button variant="contained" color="primary" onClick={handleReship}>確認補寄</Button>
        </DialogActions>
      </Dialog>

      {/* 退刷 Dialog */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>退刷 ({refundPaymentMethod === 'line_pay' ? 'LINE Pay 退款' : '信用卡退款'})</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">訂單編號: {refundOrderId}</Typography>
            {refundTradeNo && refundPaymentMethod !== 'line_pay' && <Typography variant="body2" color="text.secondary">綠界交易編號: {refundTradeNo}</Typography>}
            {refundPaymentMethod === 'line_pay' && <Typography variant="body2" color="text.secondary">付款方式: LINE Pay</Typography>}
            <Typography variant="body2" sx={{ mt: 1 }}>訂單金額: <strong>NT${refundMaxAmount.toLocaleString()}</strong></Typography>
          </Box>
          <RadioGroup value={refundFullAmount ? 'full' : 'partial'} onChange={(e) => {
            const isFull = e.target.value === 'full';
            setRefundFullAmount(isFull);
            if (isFull) setRefundAmount(String(refundMaxAmount));
          }}>
            <FormControlLabel value="full" control={<Radio size="small" />} label="全額退刷" />
            <FormControlLabel value="partial" control={<Radio size="small" />} label="部分退刷" />
          </RadioGroup>
          {!refundFullAmount && (
            <TextField
              fullWidth size="small" type="number" label="退刷金額"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              sx={{ mt: 1 }}
              inputProps={{ min: 1, max: refundMaxAmount }}
            />
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            退刷操作無法撤銷，請確認退刷金額正確
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={processingRefund}>取消</Button>
          <Button onClick={handleRefund} variant="contained" color="warning" disabled={processingRefund}>
            {processingRefund ? '處理中...' : `確認退刷 NT$${parseInt(refundAmount || '0').toLocaleString()}`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default StorefrontOrdersPage;
